/* sp-chat.js - our own shop chat: the engine and the screen.
 *
 * Plain JavaScript. No framework, no outside library, one global: window.SPChat.
 * Everything it puts in the page is named `sp-chat`.
 *
 * It expects `sp-chat-flows.js` to have run first (the theme snippet loads both
 * deferred, in that order) and one root element in the page:
 *
 *   <div id="sp-chat-root"
 *        data-sp-chat-fn="https://.../functions/v1/shop-chat"
 *        data-sp-chat-logo="..."
 *        data-sp-chat-privacy="/policies/privacy-policy"
 *        data-sp-chat-practice=""></div>
 *
 * THE ORDER OF THE CHAT. The email is asked LAST, after the flow's own
 * questions, and an OPTIONAL PHONE NUMBER after it. Lorenzo, later on 25 October
 * 2026: "can we collect the email of the customer as the last step and after the
 * email ask them for a number too", and he chose the phone optional. This
 * REVERSES his earlier "email first": a chat stopped partway now leaves its
 * answers and no contact details, which he was asked about and accepted ("Yes,
 * email at the end"). The two questions come before the flow's thank-you where
 * it has one and before `finish` where it has none, and they are sent together
 * in one `contact` call. ORDER MANAGEMENT IS NOT CHANGED: its email still comes
 * first, because the 6-digit code is sent to it.
 *
 * IT ALSO ANSWERS THE THEME'S OWN GORGIAS COMMANDS. The theme's sections call
 * `GorgiasChat.init()`, `.open()` and `.sendMessage(<flow label>)`, listen for
 * `gorgias-widget-loaded`, and put about forty `#open-gorgias-chat` links on the
 * shop. With the Gorgias embed off all of those would quietly fall back to the
 * custom request form, so this file defines `window.GorgiasChat` ITSELF - but
 * only if there is none, so the real Gorgias widget is never overwritten. See
 * "ANSWERING THE THEME'S COMMANDS" at the foot of this file.
 *
 * AND ONE PAGE CAN BE ONE FLOW. `SPChatFlows.pages` lists the pages that are a
 * single flow and nothing else - the wedding-dress page, on Lorenzo's word - and
 * on one of those the launcher, `GorgiasChat.open()` and every chat link go
 * straight into that flow, with no home screen, no other flow and no order row.
 * It is only on for a flow that EXISTS AND HAS STEPS, so a page whose flow is
 * still waiting for his questions behaves like any other page.
 *
 * NEVER FOCUS A TEXT BOX BY ITSELF. Not on open, not after a question, not
 * after an error, and not the 6-digit code box. Gorgias does; ours does not.
 * Lorenzo: "I dont want the field focused until its pressed". There is no
 * .focus() call on a textarea or an input anywhere in this file, and that is on
 * purpose - see focusPanel().
 *
 * TWO THINGS RUN HERE. The Gorgias question flows (Customize a Ring) are a list
 * of steps from the flow file; ORDER MANAGEMENT is not, and has its own half of
 * this file, marked ORDER MANAGEMENT further down. Both save every answer as it
 * is given and both end the same way, live or by email.
 *
 * A RETURNING CUSTOMER'S OWN CHATS, two ways, on Lorenzo's word (25 October
 * 2026): "how can we preserve the chat of a customer. So that when the same
 * customer returns they see their chat hisotyr". He chose both.
 *   1. THE SAME BROWSER, with no proof of anything: this browser keeps the token
 *      of every chat it started under `sp-chat-history`, and only this browser
 *      has them. The home screen lists them above the flow rows.
 *   2. ANY DEVICE, after the 6-digit code: the server sends the verified
 *      customer's past chats with their orders, and `history_view` opens one.
 * EITHER WAY A PAST CHAT OPENS READ ONLY - the whole conversation drawn again
 * with no message box, and "Back to the start" at the foot - UNLESS it is still
 * waiting or live, when it opens exactly as it does today, able to type.
 */
(function () {
  'use strict';

  var ROOT_ID = 'sp-chat-root';
  var TOKEN_KEY = 'sp-chat-token';
  var HISTORY_KEY = 'sp-chat-history';
  var HISTORY_MAX = 20;            /* about twenty, as the design says */
  var FN_DEFAULT = 'https://ofaevaqsuppeqtrtypra.supabase.co/functions/v1/shop-chat';
  var PRIVACY_DEFAULT = '/policies/privacy-policy';

  var MAX_FILES = 5;
  var MAX_FILE_BYTES = 15 * 1024 * 1024;   /* 15 MB, as the contract says */
  var MAX_TEXT = 2000;                     /* the function caps every text at 2,000 */
  var POLL_OPEN_MS = 3000;                 /* every 3s while the chat is open */
  var POLL_SHUT_MS = 15000;                /* every 15s while it is shut */
  var PHONE_UNDER = 640;                   /* under 640px is a phone: full screen */
  var TEXTAREA_MAX = 120;

  var PRACTICE_WAIT_MS = 180;              /* a practice call still takes a moment */
  var PRACTICE_REPLY_MS = 4000;            /* "after about 4 seconds" */
  var PRACTICE_REPLY_NAME = 'Sesto Synergy';
  var PRACTICE_REPLY_BODY =
    'This is a practice reply, to show how a reply from the team looks.';

  /* The shape of an email address, not whether it exists. The function checks
     it again and refuses a malformed one. */
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;

  /* A PHONE NUMBER IS TAKEN LOOSELY, as the design says: digits with `+`,
     spaces, dashes, dots or brackets, and 7 to 20 digits in it. Nothing is
     reformatted and no country is assumed - the ERP shows what the customer
     typed. The server checks it again and refuses a bad one with `bad_phone`. */
  var PHONE_CHARS = /^[0-9+().\s-]+$/;
  var PHONE_MIN_DIGITS = 7;
  var PHONE_MAX_DIGITS = 20;
  /* 40, which is what the server and the column's own CHECK allow. A tighter cap
     here would be a `maxLength` quietly cutting the end off a number the server
     would have taken - 20 digits with brackets and spaces around them can run
     past 32 - and the customer would never see why it was refused. */
  var PHONE_MAX_CHARS = 40;

  var FILE_OK = /^image\//;

  /* The theme's own event, fired once this file is ready, and the window in
     which the same flow will not be started twice - so two document listeners
     on one page (custom-ring-hero's and ours) cannot open it twice. */
  var GORGIAS_EVENT = 'gorgias-widget-loaded';
  var SAME_FLOW_MS = 1500;

  var S = {
    ready: false,
    root: null,
    fnUrl: FN_DEFAULT,
    logoUrl: '',
    privacyUrl: PRIVACY_DEFAULT,
    practice: null,        /* null, 'online' or 'offline' */
    open: false,
    view: 'home',          /* 'home' or 'convo' */
    token: null,
    flow: null,
    stepId: null,
    phase: 'home',         /* home | flow | contact | waiting | live | over */
    email: '',
    phone: null,
    /* The email and the phone are recorded TOGETHER, in one `contact` call, at
       the end of a flow. So an email on the row means both were asked and the
       call went through, which is how a redrawn chat knows not to ask again. */
    contactDone: false,
    status: null,          /* answering | waiting | live | emailed | closed */
    lastId: 0,
    files: [],
    pollTimer: null,
    locked: false,
    scrollY: 0,
    practiceId: 0,
    practiceQueue: [],
    unread: false,
    stick: true,           /* keep the log pinned to its newest line */

    /* ORDER MANAGEMENT. `orders` is the verified customer's orders exactly as
       the server sent them, kept so "Back to my orders" can draw the list again
       without asking twice; `order` is the one they chose. `verified` is only
       ever set by a successful `orders_verify`. */
    orders: null,
    order: null,
    verified: false,

    /* CHAT HISTORY. `history` is the verified customer's past chats as the
       server sent them (after the code, so good on any device); the same
       browser's own list is not held here at all - it is read from
       localStorage every time it is drawn, so two tabs cannot disagree about
       it. `practiceHistory` stands in for that list in practice mode, which
       writes nothing anywhere. */
    history: null,
    practiceHistory: null,
    practiceStore: null,

    /* PAGE MODE. The flow key this page is, or null for every ordinary page.
       Worked out once, at boot, from `location.pathname` and the flow file's
       `pages` list - and only when that flow exists and has steps. */
    pageFlowKey: null,

    /* `resume` is in flight. Page mode waits for it rather than starting a new
       chat on top of one the customer already has going. */
    resuming: false,

    /* The last flow started by a COMMAND - the theme's `sendMessage`, a
       `[data-gorgias-flow]` button or an `#open-gorgias-chat` link - so the same
       one is not started twice by two listeners in the same moment. A row
       pressed on the home screen does not go through this. */
    lastStartKey: null,
    lastStartAt: 0
  };

  var F = null;   /* the flow file: window.SPChatFlows */
  var D = {};     /* the elements we keep hold of */

  /* ------------------------------------------------------------- small helpers */

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text !== undefined && text !== null) n.textContent = text;
    return n;
  }

  function svg(paths, extra) {
    var s = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    s.setAttribute('viewBox', (extra && extra.box) || '0 0 24 24');
    s.setAttribute('fill', (extra && extra.fill) || 'none');
    s.setAttribute('stroke', (extra && extra.stroke) || 'currentColor');
    s.setAttribute('stroke-width', (extra && extra.width) || '2');
    s.setAttribute('stroke-linecap', 'round');
    s.setAttribute('stroke-linejoin', 'round');
    s.setAttribute('aria-hidden', 'true');
    for (var i = 0; i < paths.length; i++) {
      var p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      p.setAttribute('d', paths[i]);
      s.appendChild(p);
    }
    return s;
  }

  function iconChat() {
    return svg(['M21 11.5a8.4 8.4 0 0 1-9 8.4 9.3 9.3 0 0 1-3.3-.6L3 21l1.8-4.4A8.2 8.2 0 0 1 3.6 11.5 8.4 8.4 0 0 1 12 3.1a8.4 8.4 0 0 1 9 8.4z'],
      { fill: 'none', stroke: 'currentColor', width: '1.8' });
  }
  function iconBack() { return svg(['M15 19l-7-7 7-7']); }
  function iconClose() { return svg(['M18 6 6 18', 'm6 6 12 12']); }
  function iconSend() { return svg(['M12 19V5', 'm5 12 7-7 7 7']); }
  /* the chevron on an option row, the same mark the ring page's folding cards
     carry on theirs */
  function iconChevron() { return svg(['m9 18 6-6-6-6'], { width: '1.7' }); }

  /* THE MARK AT THE START OF EACH HOME-SCREEN ROW (Lorenzo, 25 October 2026:
     "Add appropriate icon at the start of each selection to start the chat").
     The flow file names one per flow (`icon:`); a name not here draws nothing,
     so a new flow without one still gets its row. Line drawings in the same
     weight as the chevron, ink until the row is pressed, then gold.
       ring      Customize a Ring - a band, seen face on
       heart     Use Personal Materials - something of theirs
       sparkles  Create Something Original - a new design
       gold      Heirloom Gold - an ingot */
  var ICONS = {
    ring: ['M20.5 12a8.5 8.5 0 1 1-17 0 8.5 8.5 0 0 1 17 0z', 'M18 12a6 6 0 1 1-12 0 6 6 0 0 1 12 0z'],
    heart: ['M12 20.5S3.5 15.6 3.5 9.4A4.4 4.4 0 0 1 12 7.1a4.4 4.4 0 0 1 8.5 2.3c0 6.2-8.5 11.1-8.5 11.1z'],
    sparkles: ['M10 3.5l1.6 4.9 4.9 1.6-4.9 1.6L10 16.5l-1.6-4.9L3.5 10l4.9-1.6z',
               'M18 14.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z'],
    gold: ['M7 6.5h10l4.5 11h-19z', 'M8.5 10.5h7']
  };
  function iconFor(name) {
    var paths = ICONS[String(name || '')];
    return paths ? svg(paths, { width: '1.6' }) : null;
  }

  function isPhone() {
    return window.innerWidth < PHONE_UNDER;
  }

  function trim(s) { return String(s === undefined || s === null ? '' : s).trim(); }

  function cap(s) { return trim(s).slice(0, MAX_TEXT); }

  function fill(line, email) {
    return String(line).replace('{email}', email || '');
  }

  /* localStorage can throw outright in a private window or with site data
     blocked, so every read and write is wrapped. */
  function readToken() {
    try { return window.localStorage.getItem(TOKEN_KEY) || null; } catch (e) { return null; }
  }
  function writeToken(v) {
    try { window.localStorage.setItem(TOKEN_KEY, v); } catch (e) { /* nothing */ }
  }
  function dropToken() {
    try { window.localStorage.removeItem(TOKEN_KEY); } catch (e) { /* nothing */ }
  }

  /* ------------------------------------------- the chats this browser started
   *
   * A small list under `sp-chat-history`, newest first, about twenty of them:
   * { token, flow_key, started_at }. The token is the only thing that opens a
   * chat, and only this browser has these, so no proof of identity is asked for
   * or possible - which is also why "Remove from this device" exists.
   *
   * EVERY READ AND WRITE IS WRAPPED, both the storage call and the JSON: a
   * private window and blocked site data each throw outright, and a stored
   * string can be anything. A list of past chats is a convenience and must
   * never be the reason the chat itself does not open.
   *
   * PRACTICE MODE TOUCHES NOTHING. It answers from an invented list held in
   * memory, so the preview can show the screen without writing to the browser
   * the preview is being looked at in.
   */
  function readHistory() {
    if (S.practice) return S.practiceHistory || [];
    var raw = null;
    try { raw = window.localStorage.getItem(HISTORY_KEY); } catch (e) { return []; }
    if (!raw) return [];
    var list = null;
    try { list = JSON.parse(raw); } catch (e) { return []; }
    if (!list || typeof list.length !== 'number') return [];
    var out = [];
    for (var i = 0; i < list.length && out.length < HISTORY_MAX; i++) {
      var x = list[i];
      if (x && typeof x.token === 'string' && x.token) {
        out.push({
          token: x.token,
          flow_key: String(x.flow_key || ''),
          started_at: x.started_at || null
        });
      }
    }
    return out;
  }

  function writeHistory(list) {
    var kept = (list || []).slice(0, HISTORY_MAX);
    if (S.practice) { S.practiceHistory = kept; return; }
    try {
      window.localStorage.setItem(HISTORY_KEY, JSON.stringify(kept));
    } catch (e) { /* nothing */ }
  }

  /* Newest first, and never the same token twice. */
  function pushHistory(token, flowKey) {
    if (!token) return;
    var list = readHistory().filter(function (x) { return x.token !== token; });
    list.unshift({
      token: token,
      flow_key: String(flowKey || ''),
      started_at: new Date().toISOString()
    });
    writeHistory(list);
  }

  /* One chat off this device's list: used when the SERVER says that token is
     not a chat any more, so a row that cannot open is not left sitting there. */
  function forgetOne(token) {
    writeHistory(readHistory().filter(function (x) { return x.token !== token; }));
    if (readToken() === token) dropToken();
  }

  function clearHistory() {
    if (S.practice) { S.practiceHistory = []; return; }
    try { window.localStorage.removeItem(HISTORY_KEY); } catch (e) { /* nothing */ }
  }

  /* NO FONT IS FETCHED HERE ANY MORE. It used to pull Inter from Google Fonts
     the first time the chat was opened. The chat now takes the shop's own type -
     Poppins, through the theme's `--typeBasePrimary` - which is already on every
     page of the site, so there is nothing left to load and nothing to wait for.
     See the font note in sp-chat.css. */

  /* ---------------------------------------------------------- the server call */

  /* ONE door. Every POST to the function goes through here, so there is one
     place that knows about credentials, JSON and what an error looks like. */
  function api(action, payload) {
    if (S.practice) return practiceApi(action, payload);
    var body = { action: action };
    for (var k in payload) if (Object.prototype.hasOwnProperty.call(payload, k)) body[k] = payload[k];
    return fetch(S.fnUrl, {
      method: 'POST',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.text().then(function (text) {
        var data = null;
        try { data = text ? JSON.parse(text) : {}; } catch (e) { data = null; }
        if (!res.ok || !data || data.error) {
          var e2 = new Error((data && data.error) || ('shop-chat ' + action + ' ' + res.status));
          e2.status = res.status;
          /* The server answers a REFUSAL - a wrong code, say - with 400 and a body
             that names it ({ok:false, state:'bad_code'}). That is an answer, not a
             fault, so the body travels with the error and a caller that expects a
             refusal can read it. (Found 25 Oct 2026: practice mode resolves
             refusals, so a wrong code passed every practice test and would have
             said "Something went wrong" against the real server.) */
          e2.data = data;
          throw e2;
        }
        return data;
      });
    });
  }

  /* PRACTICE MODE. No server is called at all: every action is answered here.
     It is on when the page says so (the preview forces it) or when the address
     carries ?sp_chat_practice=online or =offline. */
  function practiceApi(action, payload) {
    return new Promise(function (resolve) {
      window.setTimeout(function () {
        resolve(practiceAnswer(action, payload));
      }, PRACTICE_WAIT_MS);
    });
  }

  function practiceAnswer(action, p) {
    var online = S.practice === 'online';
    if (action === 'status') return { online: online };
    if (action === 'start') return { token: 'practice-' + Date.now().toString(16) };
    if (action === 'answer') return { ok: true };
    if (action === 'upload_urls') {
      return {
        uploads: (p.files || []).map(function (f, i) {
          /* no signed_url: practice keeps the file in the browser and PUTs nothing */
          return { path: 'practice/' + i + '-' + f.name, signed_url: null };
        })
      };
    }
    if (action === 'uploaded') return { ok: true };
    /* THE EMAIL AND THE PHONE AT THE END OF A FLOW, practised. The browser has
       already checked the shape of both, so nothing malformed reaches here. TWO
       SIGNPOSTED ANSWERS let the preview show the SERVER'S own refusals, which
       the browser's own checks would otherwise hide: the address
       `refuse@example.com` comes back `bad_email`, and the number `0000000`
       comes back `bad_phone`. They are written on the preview page. */
    if (action === 'contact') {
      var pEmail = String((p && p.email) || '');
      var pPhone = p && p.phone ? String(p.phone) : null;
      if (pEmail.toLowerCase() === 'refuse@example.com') {
        return { ok: false, state: 'bad_email' };
      }
      if (pPhone && pPhone.replace(/\D/g, '') === '0000000') {
        return { ok: false, state: 'bad_phone' };
      }
      return { ok: true };
    }
    if (action === 'finish') {
      if (online) {
        S.status = 'waiting';
        window.setTimeout(function () {
          S.practiceQueue.push({
            id: ++S.practiceId,
            sender: 'team',
            name: PRACTICE_REPLY_NAME,
            body: PRACTICE_REPLY_BODY,
            created_at: new Date().toISOString()
          });
          S.status = 'live';
        }, PRACTICE_REPLY_MS);
      } else {
        S.status = 'emailed';
      }
      return { mode: online ? 'live' : 'email' };
    }
    if (action === 'send') {
      /* a further message gets no practice reply */
      return { ok: true, id: ++S.practiceId };
    }
    if (action === 'poll') {
      var out = S.practiceQueue;
      S.practiceQueue = [];
      return { status: S.status, messages: out };
    }
    if (action === 'resume') {
      /* One of the invented PAST chats, if that is the token being asked about:
         the preview has to be able to open one and see a whole conversation. */
      var kept = practiceEntry('token', p && p.token);
      if (kept) return kept.transcript;
      return { status: S.status, messages: [] };
    }
    if (action === 'history_list') {
      return { ok: true, history: practiceServerHistory() };
    }
    if (action === 'history_view') {
      var seen = practiceEntry('id', p && p.conversation_id);
      if (!seen) return { ok: false, state: 'not_yours' };
      var copy = {};
      for (var key in seen.transcript) {
        if (Object.prototype.hasOwnProperty.call(seen.transcript, key)) {
          copy[key] = seen.transcript[key];
        }
      }
      copy.read_only = true;
      copy.conversation_id = seen.id;
      return copy;
    }

    /* ORDER MANAGEMENT, practised. Three invented orders come from the flow
       file, and ANY 6-DIGIT CODE IS ACCEPTED - except three signposted ones, so
       the preview can also show what a wrong, an expired and a stopped code look
       like. They are written on the preview page itself:
         000000 wrong, 000001 run out, 000002 too many tries. */
    if (action === 'orders_start') {
      S.status = 'answering';
      return { ok: true, token: 'practice-' + Date.now().toString(16) };
    }
    if (action === 'orders_resend') return { ok: true };
    if (action === 'orders_verify') {
      var code = String((p && p.code) || '');
      if (code === '000000') return { ok: false, state: 'bad_code' };
      if (code === '000001') return { ok: false, state: 'expired' };
      if (code === '000002') return { ok: false, state: 'locked' };
      if (!/^\d{6}$/.test(code)) return { ok: false, state: 'bad_code' };
      return {
        ok: true,
        orders: F.practiceOrders(Date.now()),
        history: practiceServerHistory()
      };
    }
    if (action === 'orders_list') return { ok: true, orders: S.orders || F.practiceOrders(Date.now()) };
    if (action === 'order_choose') {
      var want = (S.orders || []).filter(function (o) { return o.id === p.order_id; })[0];
      return want ? { ok: true, order: want } : { ok: false, error: 'no such order' };
    }
    if (action === 'orders_done') {
      S.status = 'closed';
      return { ok: true };
    }
    return {};
  }

  /* The invented past chats, as a practice run sees them. They are made once per
     page load from the flow file, so the dates never go stale, and they are kept
     in memory only - practice mode writes nothing to the browser. */
  function practiceChats() {
    if (!F.practiceHistory) return [];
    if (!S.practiceStore) S.practiceStore = F.practiceHistory(Date.now());
    return S.practiceStore;
  }

  function practiceEntry(key, value) {
    var all = practiceChats();
    for (var i = 0; i < all.length; i++) {
      if (String(all[i][key]) === String(value)) return all[i];
    }
    return null;
  }

  /* The shape the SERVER sends after the code: no tokens in it, because on
     another device there are none to have. */
  function practiceServerHistory() {
    return practiceChats().map(function (x) {
      return {
        id: x.id,
        flow_key: x.flow_key,
        started_at: x.started_at,
        status: x.status,
        last_at: x.last_at
      };
    });
  }

  /* And the shape THIS BROWSER would have kept: just the token, the flow and
     when it started. */
  function practiceOwnHistory() {
    return practiceChats().map(function (x) {
      return { token: x.token, flow_key: x.flow_key, started_at: x.started_at };
    });
  }

  /* ------------------------------------------------------------ building it */

  function build() {
    var root = S.root;
    root.classList.add('sp-chat');
    root.innerHTML = '';

    D.launcher = el('button', 'sp-chat-launcher');
    D.launcher.type = 'button';
    D.launcher.setAttribute('aria-label', F.ui.open);
    D.launcher.setAttribute('aria-expanded', 'false');
    D.launcher.appendChild(iconChat());
    D.dot = el('span', 'sp-chat-launcher-dot');
    D.dot.hidden = true;
    D.launcher.appendChild(D.dot);
    D.launcher.addEventListener('click', function () {
      if (S.open) closeChat(); else openChat();
    });

    D.panel = el('div', 'sp-chat-panel');
    D.panel.hidden = true;
    D.panel.setAttribute('role', 'dialog');
    D.panel.setAttribute('aria-modal', 'true');
    D.panel.setAttribute('aria-label', F.home.title);
    D.panel.setAttribute('tabindex', '-1');

    /* A REAL HEADER BAR: back, the small round logo, the title, the practice
       badge, then the close button. The logo and the title are hidden by the
       stylesheet on the home screen, which has a big logo and the same title of
       its own - see `.sp-chat-at-home` in sp-chat.css. NO NEW WORDS: the title is
       `ui.home_title`, the words already on the home screen and the launcher. */
    var head = el('div', 'sp-chat-header');
    D.back = el('button', 'sp-chat-round');
    D.back.type = 'button';
    D.back.setAttribute('aria-label', F.ui.back);
    D.back.appendChild(iconBack());
    D.back.hidden = true;
    D.back.addEventListener('click', goHome);

    D.headLogo = el('img', 'sp-chat-head-logo');
    D.headLogo.src = S.logoUrl;
    D.headLogo.alt = '';
    D.headLogo.setAttribute('aria-hidden', 'true');
    D.headLogo.width = 32;
    D.headLogo.height = 32;
    if (!S.logoUrl) D.headLogo.hidden = true;

    /* the title is said once, by the panel's own aria-label, so this copy is
       hidden from a reader rather than read out a second time */
    D.headTitle = el('span', 'sp-chat-head-title', F.home.title);
    D.headTitle.setAttribute('aria-hidden', 'true');

    D.badge = el('span', 'sp-chat-practice-badge', F.ui.practice);
    D.badge.hidden = true;

    var spacer = el('div', 'sp-chat-header-spacer');

    D.close = el('button', 'sp-chat-round');
    D.close.type = 'button';
    D.close.setAttribute('aria-label', F.ui.close);
    D.close.appendChild(iconClose());
    D.close.addEventListener('click', closeChat);

    head.appendChild(D.back);
    head.appendChild(D.headLogo);
    head.appendChild(D.headTitle);
    head.appendChild(D.badge);
    head.appendChild(spacer);
    head.appendChild(D.close);

    /* body: the home screen and the conversation, one shown at a time */
    D.body = el('div', 'sp-chat-body');

    D.home = el('div', 'sp-chat-home');
    D.homeLogo = logoImg();
    D.home.appendChild(D.homeLogo);
    D.home.appendChild(el('h2', 'sp-chat-title', F.home.title));
    /* THE PAST CHATS GO ABOVE THE FLOW ROWS. Hidden outright when there are
       none, so a first-time visitor sees exactly the screen Lorenzo has already
       seen. */
    D.history = el('div', 'sp-chat-history-block');
    D.history.hidden = true;
    D.home.appendChild(D.history);
    D.flowlist = el('div', 'sp-chat-flowlist');
    D.home.appendChild(D.flowlist);

    D.convo = el('div', 'sp-chat-convo');
    D.convo.hidden = true;
    D.convoLogo = logoImg();
    D.convo.appendChild(D.convoLogo);
    D.log = el('div', 'sp-chat-log');
    /* new lines are announced; nothing here is ever re-written in place, only
       appended, so a reader is told once */
    D.log.setAttribute('aria-live', 'polite');
    D.log.setAttribute('role', 'log');
    D.convo.appendChild(D.log);

    D.body.appendChild(D.home);
    D.body.appendChild(D.convo);

    /* foot: whatever is being asked for, then Privacy */
    var foot = el('div', 'sp-chat-foot');
    D.compose = el('div', 'sp-chat-compose');
    var prow = el('div', 'sp-chat-privacy-row');
    D.privacy = el('a', 'sp-chat-privacy', F.ui.privacy);
    D.privacy.href = S.privacyUrl;
    D.privacy.target = '_blank';
    D.privacy.rel = 'noopener';
    prow.appendChild(D.privacy);
    foot.appendChild(D.compose);
    foot.appendChild(prow);

    D.panel.appendChild(head);
    D.panel.appendChild(D.body);
    D.panel.appendChild(foot);

    root.appendChild(D.launcher);
    root.appendChild(D.panel);

    renderFlowList();
    renderHistory();
    pinToBottom();
  }

  function logoImg() {
    var img = el('img', 'sp-chat-logo');
    img.src = S.logoUrl;
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    img.width = 108;
    img.height = 108;
    if (!S.logoUrl) img.hidden = true;
    return img;
  }

  /* One option row: the label, and the chevron on the right that says it opens
     something - the ring product page's folding cards, which is what the home
     screen is drawn as now. */
  function flowRow(cls, label, iconName) {
    var b = el('button', cls);
    b.type = 'button';
    var icon = iconFor(iconName);
    if (icon) {
      var mark = el('span', 'sp-chat-flow-icon');
      mark.appendChild(icon);
      b.appendChild(mark);
    }
    b.appendChild(el('span', 'sp-chat-flow-label', label));
    var chev = el('span', 'sp-chat-flow-chev');
    chev.appendChild(iconChevron());
    b.appendChild(chev);
    return b;
  }

  /* Whether the order row is offered at all: the flow file's `show_orders`,
     false since 25 October 2026 on Lorenzo's word. Anything but an explicit
     false shows it, so a flow file written before the switch still does. */
  function ordersShown() {
    return !!(F.home.orders && F.flows[F.home.orders] && F.home.show_orders !== false);
  }

  function renderFlowList() {
    D.flowlist.innerHTML = '';
    /* PAGE MODE HAS NO HOME SCREEN AT ALL - no other flow and no order row -
       so there is nothing to draw. The home screen is never shown on such a
       page; this is here so that it cannot be, even by accident. */
    if (S.pageFlowKey) return;
    var keys = F.home.flows || [];
    for (var i = 0; i < keys.length; i++) {
      (function (key) {
        var flow = F.flows[key];
        if (!flow) return;
        var b = flowRow('sp-chat-flow', flow.label, flow.icon);
        b.addEventListener('click', function () { pickFlow(key); });
        D.flowlist.appendChild(b);
      }(keys[i]));
    }
    /* The order row, UNDER the flow rows, because it is not one of Gorgias's
       question flows: it asks for an email, sends a code and then shows what the
       ERP holds. It is the next row of the same card, divided from the one above
       it by that card's own hairline. NOT DRAWN while the flow file hides it. */
    var orders = ordersShown() && F.flows[F.home.orders];
    if (!orders) return;
    var p = flowRow('sp-chat-flow sp-chat-flow-orders', orders.label);
    p.addEventListener('click', function () { pickOrders(); });
    D.flowlist.appendChild(p);
  }

  /* ------------------------------------------------------ the past chats, drawn
   *
   * ONE DRAWING FOR BOTH WAYS IN. The home screen's list comes out of this
   * browser's localStorage and is opened by its token; the list after the
   * 6-digit code comes from the server and is opened by its conversation id.
   * They carry different keys and the same two facts - which flow it was and
   * when it started - so they are drawn by the same function and look the same.
   */

  /* The flow's own name. A key the flow file no longer knows is drawn as the key
     itself rather than as an empty row, so a flow that is ever renamed leaves
     something a customer can still press. */
  function historyLabel(key) {
    var flow = F.flows[String(key || '')];
    return (flow && flow.label) || String(key || '');
  }

  function historyList(items, onOpen) {
    var wrap = el('div', 'sp-chat-history');
    wrap.appendChild(el('h3', 'sp-chat-history-title', F.ui.previous_chats));
    var list = el('div', 'sp-chat-history-list');
    items.forEach(function (item) {
      var b = el('button', 'sp-chat-hist');
      b.type = 'button';
      b.appendChild(el('span', 'sp-chat-hist-label', historyLabel(item.flow_key)));
      b.appendChild(el('span', 'sp-chat-hist-when', dayWords(item.started_at)));
      b.addEventListener('click', function () { onOpen(item); });
      list.appendChild(b);
    });
    wrap.appendChild(list);
    return wrap;
  }

  /* The home screen's own list, with the quiet way to clear it underneath. */
  function renderHistory() {
    if (!D.history) return;
    D.history.innerHTML = '';
    var items = readHistory();
    D.history.hidden = items.length === 0;
    if (!items.length) return;
    D.history.appendChild(historyList(items, openPast));
    var row = el('div', 'sp-chat-history-foot');
    var forget = el('button', 'sp-chat-quiet', F.ui.forget_device);
    forget.type = 'button';
    forget.addEventListener('click', forgetDevice);
    row.appendChild(forget);
    D.history.appendChild(row);
  }

  /* "Remove from this device". IT DELETES NOTHING ON THE SERVER - the chats are
     still there, and the same customer can still reach them anywhere with the
     6-digit code. It exists because a shared family computer would otherwise
     show the next person somebody's chats, which is the surprise-ring worry
     Lorenzo raised about orders.

     The CURRENT chat's token goes with the list, unless that chat is still
     going: a finished one has nothing to come back to, and leaving its token
     would quietly put it back on the next page. */
  function forgetDevice() {
    clearHistory();
    if (!(S.status === 'answering' || S.status === 'waiting' || S.status === 'live')) {
      dropToken();
    }
    renderHistory();
    focusPanel();
  }

  /* Opening one of THIS BROWSER'S past chats: `resume` by its token.
     - still waiting or live: it opens exactly as it does today, able to type;
     - still part way through the questions (`answering`): it carries on from
       the next question, exactly as it does when carried onto another page.
       Lorenzo, 25 October 2026: start a chat, answer one question, go back to
       the home screen and open it again from "Your previous chats" - "it does
       not allow me continue with it. Its giving me an option to back to start
       only", because this used to draw every chat that was not waiting or live
       as a finished one;
     - anything else: the whole conversation, read only.
     A token the SERVER says is not a chat is dropped from the list rather than
     left as a row that cannot open. A call that simply failed is not: that is a
     bad moment, not a missing chat, so it offers Try again. */
  function openPast(entry) {
    var token = entry && entry.token;
    if (!token) return;
    D.log.innerHTML = '';
    atHome(false);
    clearCompose();
    addMine(historyLabel(entry.flow_key));
    api('resume', { token: token }).then(function (res) {
      var key = (res && (res.flow_key || res.flow)) || entry.flow_key;
      if (!res || !res.status) { forgetOne(token); goHome(); return; }
      if (res.status === 'waiting' || res.status === 'live' || res.status === 'answering') {
        S.token = token;
        S.lastId = 0;
        if (!S.practice) writeToken(token);
        if (key === F.home.orders) redrawOrders(res); else redraw(res);
        return;
      }
      /* A finished chat is not the current one: the token is not written back,
         so the next page shows the home screen and not this. */
      S.token = null;
      stopPoll();
      drawPast(res, key, false);
    }).catch(function (e) {
      if (e && e.data && e.data.state) { forgetOne(token); goHome(); return; }
      composeBackToStart();
      addTrouble(function () { openPast(entry); });
    });
  }

  /* ONE PAST CHAT, READ ONLY. The questions come back from the flow file by step
     id (the server keeps the answers and never the questions), the team's
     replies and the system's lines follow, photos are drawn as their names
     because a signed link is the ERP's business and not the shop's - and there
     is no message box at all, only the way back. */
  function drawPast(res, key, keepOrders) {
    var isOrders = String(key || '') === F.home.orders;
    var flow = F.flows[String(key || '')] || F.flows[F.home.flows[0]];
    var wasLastId = S.lastId;
    S.phase = 'past';
    S.status = (res && res.status) || null;
    S.email = (res && res.email) || '';
    S.phone = (res && res.phone) || null;
    D.log.innerHTML = '';
    atHome(false);
    if (!isPageFlow(String(key || ''))) addMine((flow && flow.label) || String(key || ''));

    var msgs = (res && res.messages) || [];
    var answers = (res && res.answers) || {};
    var loose;
    if (isOrders) {
      /* AN ORDERS CHAT ASKED FOR THE EMAIL FIRST and still does: the 6-digit
         code is sent to it. */
      addAsk(F.strings.email_bold, F.strings.email_italic);
      if (S.email) addMine(S.email);
      loose = drawOrderAnswers(msgs, answers);
    } else {
      /* A QUESTION FLOW ASKED FOR IT LAST: the questions, then the email and the
         phone, then the thank-you. */
      var walked = drawFlowAnswers(flow, msgs, answers);
      drawContactAnswers(res);
      if (walked.atThanks) addAskStep(walked.atThanks);
      loose = walked.loose;
    }
    drawLoose(loose);

    /* Reading an old chat must not move where the CURRENT one has got to. */
    S.lastId = wasLastId;

    clearCompose();
    composeBackToStart(keepOrders ? backToOrdersExtra() : null);
  }

  /* ------------------------------------------------------- opening and closing */

  function openChat() {
    if (S.open) {
      /* Already open and asked to open again - the theme's own
         `GorgiasChat.open()` does this. On a page-mode page that still has to
         land in the flow. */
      if (S.pageFlowKey) enterPageFlow();
      return;
    }
    S.open = true;
    S.unread = false;
    D.dot.hidden = true;
    D.panel.hidden = false;
    D.launcher.setAttribute('aria-expanded', 'true');
    /* ON A PAGE-MODE PAGE THE LAUNCHER GOES STRAIGHT INTO THE FLOW. There is no
       home screen to show, so it is never drawn, not even for a moment. */
    if (S.pageFlowKey && !enterPageFlow()) atHome(false);
    else if (!S.pageFlowKey) atHome(S.view === 'home');
    if (isPhone()) lockPage(true);
    fitViewport();
    focusPanel();
    scrollDown();
    /* A webfont that is still arriving makes the lines taller after the first
       scroll, which left the newest message just under the box. Scroll again
       once the page's own type has settled. */
    try { if (document.fonts && document.fonts.ready) document.fonts.ready.then(scrollDown); } catch (e) { /* nothing */ }
    restartPoll();
  }

  function closeChat() {
    if (!S.open) return;
    S.open = false;
    D.panel.hidden = true;
    D.launcher.setAttribute('aria-expanded', 'false');
    lockPage(false);
    restartPoll();
    try { D.launcher.focus({ preventScroll: true }); } catch (e) { D.launcher.focus(); }
  }

  /* The panel itself takes focus, NOT a text box. The panel is a div with
     tabindex="-1": a reader lands in the dialog, and no keyboard opens. */
  function focusPanel() {
    try { D.panel.focus({ preventScroll: true }); } catch (e) {
      try { D.panel.focus(); } catch (e2) { /* nothing */ }
    }
  }

  function atHome(yes) {
    S.view = yes ? 'home' : 'convo';
    D.home.hidden = !yes;
    D.convo.hidden = yes;
    /* THE BACK ARROW IS NOT THERE AT ALL IN PAGE MODE: it leads to the home
       screen, and on such a page there is no home screen to lead to. */
    D.back.hidden = yes || !!S.pageFlowKey;
    D.panel.classList.toggle('sp-chat-at-home', yes);
    if (yes) { D.compose.innerHTML = ''; renderHistory(); }
  }

  function goHome() {
    /* In page mode "home" IS the flow. */
    if (S.pageFlowKey) { reset(); return; }
    atHome(true);
    focusPanel();
  }

  /* Start again from nothing: used by the back arrow's end-of-chat button and
     by the preview when it switches practice mode. */
  function reset() {
    stopPoll();
    S.token = null;
    S.flow = null;
    S.stepId = null;
    S.phase = 'home';
    S.email = '';
    S.phone = null;
    S.contactDone = false;
    S.status = null;
    S.lastId = 0;
    S.files = [];
    S.practiceId = 0;
    S.practiceQueue = [];
    S.orders = null;
    S.order = null;
    S.verified = false;
    /* `history` is this conversation's copy of the server's list and goes with
       it. THE BROWSER'S OWN LIST IS NOT TOUCHED: it is not part of a chat, it is
       part of the device, and only "Remove from this device" clears it. */
    S.history = null;
    D.log.innerHTML = '';
    D.compose.innerHTML = '';
    /* IN PAGE MODE "START AGAIN" MEANS THE SAME FLOW AGAIN, not a home screen.
       pickFlow draws it and opens a new conversation, exactly as pressing the
       row does on an ordinary page. */
    if (S.pageFlowKey) { pickFlow(S.pageFlowKey); return; }
    atHome(true);
  }

  function lockPage(on) {
    var b = document.body;
    if (!b) return;
    if (on) {
      if (S.locked) return;
      S.scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      b.classList.add('sp-chat-page-locked');
      b.style.top = (-S.scrollY) + 'px';
      S.locked = true;
    } else {
      if (!S.locked) return;
      b.classList.remove('sp-chat-page-locked');
      b.style.top = '';
      window.scrollTo(0, S.scrollY);
      S.locked = false;
    }
  }

  /* The iPhone keyboard shrinks the visual viewport without moving the layout
     one, so a fixed panel ends up under the keys. These two custom properties
     put it back on top of them. */
  function fitViewport() {
    var vv = window.visualViewport;
    var style = S.root.style;
    if (!vv || !isPhone() || !S.open) {
      style.removeProperty('--sp-chat-h');
      style.removeProperty('--sp-chat-bottom');
      return;
    }
    style.setProperty('--sp-chat-h', Math.round(vv.height) + 'px');
    var below = window.innerHeight - vv.height - vv.offsetTop;
    style.setProperty('--sp-chat-bottom', Math.max(0, Math.round(below)) + 'px');
  }

  /* Keep the newest line in view. Lines keep growing for a moment after they
     are added (the font arriving, a picture's box, the message box below), so
     a single scroll lands short - 43px short was measured on 25 Oct 2026. The
     body therefore stays PINNED to the bottom while it settles, until the
     customer scrolls up to read something, when it lets go. */
  function scrollDown() {
    S.stick = true;
    window.requestAnimationFrame(function () {
      D.body.scrollTop = D.body.scrollHeight;
    });
  }

  function pinToBottom() {
    D.body.addEventListener('scroll', function () {
      S.stick = D.body.scrollHeight - D.body.scrollTop - D.body.clientHeight < 60;
    }, { passive: true });
    if (!window.ResizeObserver) return;
    var ro = new window.ResizeObserver(function () {
      if (S.stick) D.body.scrollTop = D.body.scrollHeight;
    });
    ro.observe(D.log);
    ro.observe(D.body);
  }

  /* --------------------------------------------------------- what is on screen */

  /* A QUESTION: the first line in bold, the second under it. The second line is
     ITALIC AND MUTED unless `style` says 'plain', when it is drawn in the body
     colour and not italic - which is how Gorgias has some of the second lines in
     Use Personal Materials, Create Something Original and Heirloom Gold. Every
     call that passes no style gets italic, so nothing written before this
     existed changed. */
  function addAsk(bold, second, style) {
    var n = el('div', 'sp-chat-ask');
    n.appendChild(el('strong', null, bold));
    if (second) {
      n.appendChild(style === 'plain'
        ? el('span', 'sp-chat-ask-plain', second)
        : el('em', null, second));
    }
    D.log.appendChild(n);
    scrollDown();
    return n;
  }

  /* One step's two lines, with whichever manner the flow file gives its second
     one. Everything that draws a step goes through here, so the live run and
     a chat drawn again cannot come to disagree about an italic. */
  function addAskStep(step) {
    return addAsk(step.bold, step.italic, step.second_style);
  }

  function addMine(text) {
    var row = el('div', 'sp-chat-mine');
    var box = el('div', 'sp-chat-mine-box');
    box.appendChild(el('span', 'sp-chat-mine-text', text));
    row.appendChild(box);
    D.log.appendChild(row);
    scrollDown();
    return row;
  }

  function addMineFiles(files) {
    var row = el('div', 'sp-chat-mine');
    var box = el('div', 'sp-chat-sent-files');
    for (var i = 0; i < files.length; i++) {
      var f = files[i];
      var cell = el('div', 'sp-chat-sent-file');
      if (f.preview) {
        cell.style.backgroundImage = 'url("' + f.preview + '")';
      } else {
        cell.textContent = f.name;
      }
      cell.title = f.name;
      box.appendChild(cell);
    }
    row.appendChild(box);
    D.log.appendChild(row);
    scrollDown();
    return row;
  }

  function addTeam(name, body) {
    var n = el('div', 'sp-chat-them');
    if (name) n.appendChild(el('span', 'sp-chat-them-name', name));
    n.appendChild(el('div', 'sp-chat-them-body', body));
    D.log.appendChild(n);
    scrollDown();
    return n;
  }

  function addSystem(text) {
    var n = el('div', 'sp-chat-system', text);
    D.log.appendChild(n);
    scrollDown();
    return n;
  }

  /* An automated reply: Gorgias's own answer to an option, or to Track order or
     Return order. It is on the left like a question but it is not asking
     anything, so it is not bold and its LINE BREAKS ARE KEPT - the return reply
     has four paragraphs and an address on a line of its own, and it is read by
     somebody who may have to post a ring to that address. */
  function addBot(text) {
    var n = el('div', 'sp-chat-bot', text);
    D.log.appendChild(n);
    scrollDown();
    return n;
  }

  /* A call failed. Say so calmly, offer the same thing again, and NEVER throw
     away what the customer typed: `again` is the whole step, re-run. */
  function addTrouble(again) {
    var n = el('div', 'sp-chat-trouble');
    n.appendChild(document.createTextNode(F.ui.error));
    var b = el('button', 'sp-chat-retry', F.ui.retry);
    b.type = 'button';
    b.addEventListener('click', function () {
      if (n.parentNode) n.parentNode.removeChild(n);
      again();
    });
    n.appendChild(document.createElement('br'));
    n.appendChild(b);
    D.log.appendChild(n);
    scrollDown();
    return n;
  }

  /* ------------------------------------------------------------ the composers */

  function clearCompose() { D.compose.innerHTML = ''; }

  /* The message box. `onSend` gets the text. The box is NEVER focused here.
     `extras` draws quiet buttons under it - see addQuietRow. */
  function composeBox(onSend, extras) {
    clearCompose();
    var box = el('div', 'sp-chat-box');
    var ta = el('textarea', 'sp-chat-input');
    ta.rows = 1;
    ta.placeholder = F.ui.placeholder;
    ta.setAttribute('aria-label', F.ui.placeholder);
    ta.maxLength = MAX_TEXT;
    var btn = el('button', 'sp-chat-send');
    btn.type = 'button';
    btn.setAttribute('aria-label', F.ui.send);
    btn.appendChild(iconSend());
    btn.disabled = true;

    var hint = el('div', 'sp-chat-hint');
    hint.hidden = true;

    function grow() {
      ta.style.height = 'auto';
      ta.style.height = Math.min(TEXTAREA_MAX, ta.scrollHeight) + 'px';
    }

    function go() {
      var text = cap(ta.value);
      if (!text) return;
      hint.hidden = true;
      onSend(text, {
        /* the step may refuse it (a bad email): then the words stay put */
        keep: function (message) {
          hint.textContent = message;
          hint.hidden = false;
          btn.disabled = false;
          scrollDown();
        },
        taken: function () {
          ta.value = '';
          grow();
          btn.disabled = true;
        }
      });
    }

    ta.addEventListener('input', function () {
      btn.disabled = !trim(ta.value);
      hint.hidden = true;
      grow();
    });

    ta.addEventListener('keydown', function (ev) {
      /* Enter sends and Shift+Enter makes a new line - on a desktop. On a phone
         Enter is the keyboard's own return key and makes a new line. */
      if (ev.key === 'Enter' && !ev.shiftKey && !isPhone()) {
        ev.preventDefault();
        go();
      }
    });

    btn.addEventListener('click', go);

    box.appendChild(ta);
    box.appendChild(btn);
    D.compose.appendChild(box);
    D.compose.appendChild(hint);
    if (extras) addQuietRow(extras);
    scrollDown();
    return { box: box, input: ta, button: btn, hint: hint };
  }

  /* `extras` is for the quiet buttons that sit UNDER the pills rather than among
     them - "Back to my orders" is the only one. They are drawn in their own row
     so a way out of the question is never mistaken for an answer to it. */
  function composeChoices(step, onPick, extras) {
    clearCompose();
    var row = el('div', 'sp-chat-choices');
    (step.choices || []).forEach(function (choice) {
      var b = el('button', 'sp-chat-choice', choice.label);
      b.type = 'button';
      b.addEventListener('click', function () {
        clearCompose();
        onPick(choice);
      });
      row.appendChild(b);
    });
    D.compose.appendChild(row);
    if (extras) addQuietRow(extras);
    scrollDown();
  }

  function addQuietRow(extras) {
    var row = el('div', 'sp-chat-quietrow');
    extras.forEach(function (x) {
      var b = el('button', 'sp-chat-quiet', x.label);
      b.type = 'button';
      /* the composer is NOT cleared here: "Send a new code" has to leave the code
         box exactly where it is. Whatever replaces the composer clears it. */
      b.addEventListener('click', function () { x.go(); });
      row.appendChild(b);
    });
    D.compose.appendChild(row);
    return row;
  }

  /* THE PHONE BOX, with Skip beside it. `onDone` gets the number, or null when
     Skip is pressed. A telephone keypad on a phone and the browser's own phone
     autofill - and IT IS NOT FOCUSED, by Lorenzo's standing rule, so no keyboard
     opens until it is pressed. */
  function composePhone(onDone) {
    clearCompose();
    var row = el('div', 'sp-chat-phonerow');
    var box = el('div', 'sp-chat-box sp-chat-phonebox');
    var input = el('input', 'sp-chat-input sp-chat-phone');
    /* a text box, as the design says, with the telephone keypad and autofill
       asked for by `inputmode` and `autocomplete` rather than by `type` - so
       nothing is refused by a browser's own idea of a phone number */
    input.type = 'text';
    input.setAttribute('inputmode', 'tel');
    input.setAttribute('autocomplete', 'tel');
    input.setAttribute('aria-label', F.ui.phone_label);
    input.placeholder = F.ui.phone_label;
    input.maxLength = PHONE_MAX_CHARS;

    var btn = el('button', 'sp-chat-send');
    btn.type = 'button';
    btn.setAttribute('aria-label', F.ui.send);
    btn.appendChild(iconSend());
    btn.disabled = true;

    var hint = el('div', 'sp-chat-hint');
    hint.hidden = true;

    var skip = el('button', 'sp-chat-choice sp-chat-skip', F.ui.skip);
    skip.type = 'button';

    function go() {
      var value = trim(input.value);
      if (!phoneOk(value)) {
        /* the words stay in the box: a mistyped number is corrected, not retyped */
        sayHint(hint, F.ui.bad_phone);
        return;
      }
      clearCompose();
      onDone(value);
    }

    input.addEventListener('input', function () {
      btn.disabled = !trim(input.value);
      hint.hidden = true;
    });
    input.addEventListener('keydown', function (ev) {
      if (ev.key === 'Enter') { ev.preventDefault(); if (!btn.disabled) go(); }
    });
    btn.addEventListener('click', go);
    skip.addEventListener('click', function () { clearCompose(); onDone(null); });

    box.appendChild(input);
    box.appendChild(btn);
    row.appendChild(box);
    row.appendChild(skip);
    D.compose.appendChild(row);
    D.compose.appendChild(hint);
    scrollDown();
    return { box: box, input: input, button: btn, hint: hint, skip: skip };
  }

  function composeUpload(step, onSend) {
    clearCompose();
    var max = step.max_files || MAX_FILES;
    S.files = [];

    var wrap = el('div', 'sp-chat-upload');
    var thumbs = el('div', 'sp-chat-thumbs');
    var row = el('div', 'sp-chat-upload-row');
    var hint = el('div', 'sp-chat-hint');
    hint.hidden = true;

    var input = el('input', 'sp-chat-file-input');
    input.type = 'file';
    input.multiple = true;
    input.accept = 'image/*,application/pdf';

    var pick = el('button', 'sp-chat-plain', F.ui.choose_files);
    pick.type = 'button';
    var send = el('button', 'sp-chat-plain', F.ui.send_files);
    send.type = 'button';
    send.disabled = true;

    function say(message) {
      hint.textContent = message;
      hint.hidden = false;
    }

    function draw() {
      thumbs.innerHTML = '';
      S.files.forEach(function (f, i) {
        var cell = el('div', 'sp-chat-thumb');
        if (f.preview) cell.style.backgroundImage = 'url("' + f.preview + '")';
        var name = el('div', 'sp-chat-thumb-name', f.name);
        cell.appendChild(name);
        var x = el('button', 'sp-chat-thumb-x', '×');
        x.type = 'button';
        x.setAttribute('aria-label', F.ui.remove_file + ' ' + f.name);
        x.addEventListener('click', function () {
          if (f.preview) { try { URL.revokeObjectURL(f.preview); } catch (e) { /* nothing */ } }
          S.files.splice(i, 1);
          draw();
        });
        cell.appendChild(x);
        thumbs.appendChild(cell);
      });
      pick.textContent = S.files.length ? F.ui.add_more : F.ui.choose_files;
      send.disabled = S.files.length === 0;
      scrollDown();
    }

    input.addEventListener('change', function () {
      var chosen = Array.prototype.slice.call(input.files || []);
      hint.hidden = true;
      for (var i = 0; i < chosen.length; i++) {
        var file = chosen[i];
        if (!FILE_OK.test(file.type) && file.type !== 'application/pdf') {
          say(F.ui.wrong_file_kind);
          continue;
        }
        if (file.size > MAX_FILE_BYTES) { say(F.ui.file_too_big); continue; }
        if (S.files.length >= max) { say(F.ui.too_many_files); break; }
        var preview = null;
        if (FILE_OK.test(file.type)) {
          try { preview = URL.createObjectURL(file); } catch (e) { preview = null; }
        }
        S.files.push({
          file: file, name: file.name, type: file.type, size: file.size, preview: preview
        });
      }
      input.value = '';
      draw();
    });

    pick.addEventListener('click', function () { input.click(); });
    send.addEventListener('click', function () {
      if (!S.files.length) return;
      send.disabled = true;
      pick.disabled = true;
      onSend(S.files.slice(), function undo() {
        send.disabled = false;
        pick.disabled = false;
      });
    });

    row.appendChild(pick);
    row.appendChild(send);
    wrap.appendChild(thumbs);
    wrap.appendChild(row);
    wrap.appendChild(input);
    wrap.appendChild(hint);
    D.compose.appendChild(wrap);
    draw();
  }

  /* The way back to the home screen at the end of an emailed chat, and at the
     foot of a past chat drawn read only. `extras` is the quiet row under it -
     "Back to my orders", for a verified customer reading one of their old chats
     who should not have to type the code again to get back to their orders. */
  function composeBackToStart(extras) {
    clearCompose();
    var row = el('div', 'sp-chat-choices');
    var b = el('button', 'sp-chat-plain', F.ui.back);
    b.type = 'button';
    b.addEventListener('click', function () { reset(); focusPanel(); });
    row.appendChild(b);
    D.compose.appendChild(row);
    if (extras) addQuietRow(extras);
    scrollDown();
  }

  /* -------------------------------------------------------------- the flow */

  function questionText(step) {
    return step.italic ? step.bold + '\n' + step.italic : step.bold;
  }

  /* Does this flow have questions to ask? A flow file entry can exist with no
     steps - the wedding ring flow does, deliberately, until Lorenzo gives his
     questions - and page mode is only on for one that has them. */
  function flowHasSteps(flow) {
    return !!(flow && flow.steps && flow.first && flow.steps[flow.first]);
  }

  function pickFlow(key) {
    var flow = F.flows[key];
    if (!flowHasSteps(flow)) return;
    S.flow = flow;
    S.phase = 'flow';
    S.stepId = null;
    S.email = '';
    S.phone = null;
    S.contactDone = false;
    D.log.innerHTML = '';
    atHome(false);
    /* The row that was pressed, echoed as the customer's own pill. A PAGE'S OWN
       FLOW WAS NEVER PRESSED - it is not on the home screen - so nothing is put
       in their mouth. See isPageFlow. */
    if (flow.label && !isPageFlow(flow.key)) addMine(flow.label);
    startChat();
  }

  /* A flow some page opens by itself, through F.pages. Its label names the chat
     in "Your previous chats" and in the ticket, but it is never drawn as the
     customer's opening pill - not when it starts, not when it is picked up
     again after a reload, and not when it is read back as a past chat - because
     the customer never pressed a row for it. Lorenzo named the wedding-dress
     page's flow "Wedding ring" on 25 October 2026; until then it had no label,
     and that alone had kept the pill away. */
  function isPageFlow(key) {
    var pages = F.pages || {};
    for (var path in pages) {
      if (Object.prototype.hasOwnProperty.call(pages, path) && pages[path] === key) return true;
    }
    return false;
  }

  /* THE CONVERSATION IS OPENED AT ONCE AND WITH NO EMAIL. The `email` field is
     left off the request altogether rather than sent empty, as the contract says
     ("`start` accepts no email for a question flow"). Every answer is still
     saved as it is given, so a chat stopped partway shows in the ERP with its
     answers and no contact details - which is what Lorenzo accepted when he was
     asked about this ("Yes, email at the end"). */
  function startChat() {
    api('start', {
      flow: S.flow.key,
      page: location.href
    }).then(function (res) {
      S.token = res.token;
      S.status = 'answering';
      if (!S.practice && S.token) writeToken(S.token);
      /* this browser keeps EVERY chat it started, not only the current one */
      if (S.token) pushHistory(S.token, S.flow.key);
      S.phase = 'flow';
      askStep(S.flow.first);
    }).catch(function () {
      addTrouble(function () { startChat(); });
    });
  }

  /* IS IT TIME FOR THE EMAIL AND THE PHONE? They come after the flow's own
     questions: immediately BEFORE the thank-you where the flow has one
     (`thanks_step`), and where it has none - Use Personal Materials - when the
     questions run out. Heirloom Gold opens with a message of its own, which is
     why this asks for the named thank-you step and not merely "a message". */
  function contactDueAt(stepId) {
    if (S.contactDone) return false;
    if (!S.flow || S.flow.kind === 'orders') return false;
    if (!stepId || !S.flow.steps[stepId]) return true;      /* nothing left to ask */
    return !!S.flow.thanks_step && stepId === S.flow.thanks_step;
  }

  function askStep(stepId) {
    if (contactDueAt(stepId)) {
      askContact(function () { askStep(stepId); });
      return;
    }
    var step = S.flow.steps[stepId];
    if (!step) { finishChat(); return; }
    S.stepId = stepId;
    addAskStep(step);

    if (step.kind === 'text') {
      composeBox(function (text, ctl) {
        ctl.taken();
        clearCompose();
        addMine(text);
        saveAnswer(step, text, function () { askStep(step.next); });
      });
      return;
    }

    if (step.kind === 'choices') {
      composeChoices(step, function (choice) {
        addMine(choice.label);
        saveAnswer(step, choice.label, function () { askStep(choice.next); });
      });
      return;
    }

    if (step.kind === 'upload') {
      composeUpload(step, function (files, undo) {
        sendFiles(step, files, undo);
      });
      return;
    }

    /* 'message': the bot has said it; nothing is asked */
    clearCompose();
    if (step.next) askStep(step.next); else finishChat();
  }

  /* ------------------------------------------------- the email, then the phone
   *
   * Lorenzo, 25 October 2026: "can we collect the email of the customer as the
   * last step and after the email ask them for a number too", and the phone
   * OPTIONAL. Both words are approved and both live in the flow file's
   * `strings`.
   *
   * They go to the server TOGETHER, in one `contact {token, email, phone|null}`
   * call, so there is one moment at which a chat gains its contact details and
   * not two. That is also why an email on a row is enough to know, on another
   * page, that both have already been asked.
   *
   * `done` is what happens next: the thank-you, or `finish` where the flow has
   * no thank-you.
   */
  function askContact(done) {
    S.phase = 'contact';
    askContactEmail(done, null);
  }

  /* The same approved email question, now at the end. `hint` is set only when
     the SERVER has refused the address, so the line is said under the box the
     customer is about to type in again. */
  function askContactEmail(done, hint) {
    addAsk(F.strings.email_bold, F.strings.email_italic);
    var ctl = composeBox(function (text, box) {
      var email = text.trim();
      if (!EMAIL_RE.test(email) || email.length > 254) {
        box.keep(F.ui.bad_email);
        return;
      }
      box.taken();
      clearCompose();
      addMine(email);
      S.email = email;
      askContactPhone(email, done, null);
    });
    if (hint) sayHint(ctl.hint, hint);
  }

  /* THE PHONE IS OPTIONAL, and Skip sits beside the box - the same word the
     photo question's Skip carries. A number that is not a number gets the quiet
     hint and the question is asked again, with what they typed still in the box
     so it can be corrected rather than retyped. */
  function askContactPhone(email, done, hint) {
    addAsk(F.strings.phone_bold, F.strings.phone_italic);
    var ctl = composePhone(function (phone) {
      /* Skip is echoed as the customer's own pill, exactly as pressing Skip at
         the photo question is. */
      addMine(phone === null ? F.ui.skip : phone);
      sendContact(email, phone, done);
    });
    if (hint) sayHint(ctl.hint, hint);
  }

  function sayHint(node, message) {
    if (!node) return;
    node.textContent = message;
    node.hidden = false;
    scrollDown();
  }

  /* Digits with `+`, spaces, dashes, dots or brackets, 7 to 20 digits. */
  function phoneOk(value) {
    var v = trim(value);
    if (!v || v.length > PHONE_MAX_CHARS) return false;
    if (!PHONE_CHARS.test(v)) return false;
    var digits = v.replace(/\D/g, '').length;
    return digits >= PHONE_MIN_DIGITS && digits <= PHONE_MAX_DIGITS;
  }

  /* One call records both. A REFUSAL IS AN ANSWER, NOT A FAULT: the server says
     400 with `{ok:false, state:'bad_email'|'bad_phone'}`, which arrives on
     `e.data` exactly as a wrong 6-digit code does, and the right thing is the
     question again with its own quiet line - never "Something went wrong".
     A 200 carrying `ok:false` is read the same way, so practice mode (which
     resolves everything) exercises the same path the real server takes. */
  function sendContact(email, phone, done) {
    api('contact', { token: S.token, email: email, phone: phone }).then(function (res) {
      if (res && res.ok === false) { contactRefused(res.state, email, done); return; }
      S.email = email;
      S.phone = phone || null;
      S.contactDone = true;
      done();
    }).catch(function (e) {
      var state = e && e.data && e.data.state;
      if (state) { contactRefused(state, email, done); return; }
      addTrouble(function () { sendContact(email, phone, done); });
    });
  }

  function contactRefused(state, email, done) {
    if (state === 'bad_phone') { askContactPhone(email, done, F.ui.bad_phone); return; }
    /* `bad_email`, and anything else named: the address is what has to change,
       and the phone is asked again after it because one call records both. */
    askContactEmail(done, F.ui.bad_email);
  }

  /* The email and the phone of a chat being DRAWN AGAIN - carried onto the next
     page, or opened out of "Your previous chats". Each is drawn only when it is
     there: a `phone` that is null may be a Skip or may be a server that does not
     send the field, and the two cannot be told apart, so nothing is claimed. */
  function drawContactAnswers(res) {
    var email = trim(res && res.email);
    if (!email) return false;
    addAsk(F.strings.email_bold, F.strings.email_italic);
    addMine(email);
    var phone = trim(res && res.phone);
    if (phone) {
      addAsk(F.strings.phone_bold, F.strings.phone_italic);
      addMine(phone);
    }
    return true;
  }

  /* Every answer is saved as it is given, so an unfinished chat still shows how
     far it got. */
  function saveAnswer(step, value, done) {
    api('answer', {
      token: S.token,
      step_id: step.id,
      question: questionText(step),
      value: value
    }).then(function () {
      done();
    }).catch(function () {
      addTrouble(function () { saveAnswer(step, value, done); });
    });
  }

  function sendFiles(step, files, undo) {
    var list = files.map(function (f) {
      return { name: f.name, type: f.type, size: f.size };
    });
    api('upload_urls', { token: S.token, step_id: step.id, files: list })
      .then(function (res) {
        var uploads = res.uploads || [];
        var puts = files.map(function (f, i) {
          var up = uploads[i];
          if (!up || !up.signed_url) return Promise.resolve();   /* practice mode */
          return fetch(up.signed_url, {
            method: 'PUT',
            credentials: 'omit',
            headers: { 'Content-Type': f.type || 'application/octet-stream' },
            body: f.file
          }).then(function (r) {
            if (!r.ok) throw new Error('upload ' + r.status);
          });
        });
        return Promise.all(puts).then(function () {
          return api('uploaded', {
            token: S.token,
            step_id: step.id,
            question: questionText(step),
            files: files.map(function (f, i) {
              return {
                path: (uploads[i] && uploads[i].path) || null,
                name: f.name, type: f.type, size: f.size
              };
            })
          });
        });
      })
      .then(function () {
        clearCompose();
        addMineFiles(files);
        S.files = [];
        askStep(step.next);
      })
      .catch(function () {
        /* the chosen files are still on screen and still chosen */
        if (undo) undo();
        addTrouble(function () { sendFiles(step, files, undo); });
      });
  }

  function finishChat() {
    api('finish', { token: S.token }).then(function (res) {
      if (res.mode === 'live') {
        S.phase = 'live';
        S.status = S.status === 'live' ? 'live' : 'waiting';
        addSystem(F.strings.after_thanks_live);
        liveCompose();
        restartPoll();
      } else {
        S.phase = 'over';
        S.status = 'emailed';
        addSystem(fill(F.strings.after_thanks_email, S.email));
        dropToken();
        composeBackToStart();
        stopPoll();
      }
    }).catch(function () {
      addTrouble(finishChat);
    });
  }

  /* The message box stays open while the chat is waiting or live. */
  function liveCompose() {
    composeBox(function (text, ctl) {
      ctl.taken();
      sendLive(text, ctl);
    });
  }

  function sendLive(text, ctl) {
    var row = addMine(text);
    api('send', { token: S.token, text: text }).then(function (res) {
      if (res && res.id) S.lastId = Math.max(S.lastId, res.id);
    }).catch(function () {
      /* the words are not lost: Try again sends exactly the same text */
      if (row && row.parentNode) row.parentNode.removeChild(row);
      addTrouble(function () { sendLive(text, ctl); });
    });
  }

  /* --------------------------------------------------------------- polling */

  function stopPoll() {
    if (S.pollTimer) { window.clearTimeout(S.pollTimer); S.pollTimer = null; }
  }

  function pollWanted() {
    return !!S.token && (S.status === 'waiting' || S.status === 'live');
  }

  function restartPoll() {
    stopPoll();
    if (!pollWanted()) return;
    S.pollTimer = window.setTimeout(tick, S.open ? POLL_OPEN_MS : POLL_SHUT_MS);
  }

  function tick() {
    S.pollTimer = null;
    if (!pollWanted()) return;
    api('poll', { token: S.token, after: S.lastId })
      .then(function (res) { takePoll(res); })
      .catch(function () { /* a missed poll is not worth a line on screen */ })
      .then(function () { restartPoll(); });
  }

  function takePoll(res) {
    if (!res) return;
    var msgs = res.messages || [];
    for (var i = 0; i < msgs.length; i++) {
      var m = msgs[i];
      if (m.id && m.id > S.lastId) S.lastId = m.id;
      if (m.sender === 'team') {
        addTeam(m.name, m.body);
        if (!S.open) { S.unread = true; D.dot.hidden = false; }
      } else if (m.sender === 'system') {
        addSystem(m.body);
      } else if (m.sender === 'bot') {
        addSystem(m.body);
      }
    }
    if (res.status) {
      var was = S.status;
      S.status = res.status;
      if (S.status === 'emailed' && was !== 'emailed') {
        /* the function has already sent the system message that says so; if it
           did not, say the line ourselves rather than leave the chat silent */
        if (!hasSystemFrom(msgs)) addSystem(fill(F.strings.no_answer, S.email));
        endChat();
      } else if (S.status === 'closed' && was !== 'closed') {
        if (!hasSystemFrom(msgs)) addSystem(F.strings.chat_ended);
        endChat();
      }
    }
  }

  function hasSystemFrom(msgs) {
    for (var i = 0; i < msgs.length; i++) if (msgs[i].sender === 'system') return true;
    return false;
  }

  function endChat() {
    S.phase = 'over';
    stopPoll();
    dropToken();
    composeBackToStart();
  }

  /* --------------------------------------------------- carrying it across pages */

  /* The token is kept so the same conversation is still there on the next page.
     A finished chat (emailed or closed) is NOT brought back: the home screen
     shows instead. */
  function resume() {
    var token = readToken();
    if (!token) return;
    S.token = token;
    /* A PAGE-MODE PAGE WAITS FOR THIS before starting its own flow, so a chat
       the customer already has going is not thrown away by opening the
       launcher. */
    S.resuming = true;
    api('resume', { token: token }).then(function (res) {
      if (!res || !res.status || res.status === 'emailed' || res.status === 'closed') {
        /* A FINISHED CHAT STAYS ON THIS BROWSER'S LIST. It is only no longer the
           CURRENT one, so its token comes out of sp-chat-token and stays in
           sp-chat-history, where "Your previous chats" can still open it. */
        S.token = null;
        dropToken();
        resumeSettled();
        return;
      }
      /* ON A PAGE-MODE PAGE ONLY THAT PAGE'S OWN FLOW IS PICKED UP. Lorenzo, 25
         October 2026, on the wedding-dress page: "I see my chat history of
         customize this ring under the wedding ring chat". A chat of another flow
         that is still going is not drawn here and not touched: the page starts
         its own flow when the chat is opened, which then becomes the current
         chat, and the other one stays on this browser's list, where "Your
         previous chats" on any other page still opens it, able to type. It is
         put on that list here if it is not on it already, because a chat
         started before the list existed (chat 60 was) would otherwise be lost
         to this browser. */
      var resumedKey = String(res.flow_key || res.flow || '');
      if (S.pageFlowKey && resumedKey !== S.pageFlowKey) {
        var kept = readHistory();
        if (!kept.some(function (x) { return x.token === token; })) {
          kept.unshift({ token: token, flow_key: resumedKey,
                         started_at: res.started_at || new Date().toISOString() });
          writeHistory(kept);
        }
        S.token = null;
        resumeSettled();
        return;
      }
      redraw(res);
      resumeSettled();
    }).catch(function (e) {
      if (e && e.data && e.data.state === 'unknown_chat') {
        /* the server says that chat is gone, so it comes off the list too */
        forgetOne(token);
        renderHistory();
        S.token = null;
        resumeSettled();
        return;
      }
      /* leave the token alone: the next page can try again */
      S.token = null;
      resumeSettled();
    });
  }

  /* `resume` has answered, one way or the other. On a page-mode page with the
     panel already open and nothing drawn, this is the moment its flow starts. */
  function resumeSettled() {
    S.resuming = false;
    if (S.pageFlowKey && S.open) enterPageFlow();
  }

  /* Redraw a conversation on the next page. The database keeps the customer's
     ANSWERS as messages (each carrying its step id) and never the questions,
     so the questions are put back from the flow file: walk the flow from its
     first step, and under each question draw the answer that step got. Then the
     lines that belong to no step - the team's replies, the system's lines and
     anything the customer typed while live - follow in the order they were
     written. (resume answers `flow_key`; `flow` is read too, in case.) */
  function redraw(res) {
    /* An orders chat is not a list of questions, so it is drawn by its own
       function - see redrawOrders. */
    if ((res.flow_key || res.flow) === F.home.orders) { redrawOrders(res); return; }
    var flow = F.flows[res.flow_key || res.flow] || F.flows[F.home.flows[0]];
    S.flow = flow;
    S.email = res.email || '';
    S.phone = (res && res.phone) || null;
    /* AN EMAIL ON THE ROW MEANS `contact` WENT THROUGH, because one call records
       the email and the phone together. So neither is asked a second time. */
    S.contactDone = !!S.email;
    S.status = res.status;
    D.log.innerHTML = '';
    atHome(false);
    if (flow.label && !isPageFlow(flow.key)) addMine(flow.label);

    var walked = drawFlowAnswers(flow, res.messages || [], res.answers || {});

    if (res.status === 'answering') {
      S.phase = 'flow';
      if (walked.waitingOn) { askAgain(walked.waitingOn); return; }
      /* Every question answered. What is left is the email and the phone, and
         then the thank-you - or, when they are already recorded, the thank-you
         and the `finish` that never landed. */
      drawContactAnswers(res);
      askStep(walked.atThanks ? walked.atThanks.id : null);
      return;
    }

    /* waiting or live: the contact details, the thank-you, the line that
       followed it, then the rest */
    drawContactAnswers(res);
    if (walked.atThanks) addAskStep(walked.atThanks);
    addSystem(F.strings.after_thanks_live);
    drawLoose(walked.loose);
    S.phase = 'live';
    liveCompose();
    restartPoll();
  }

  /* THE WALK ITSELF, shared by the chat carried onto the next page and by a past
     chat drawn read only, so the two cannot come to draw a conversation
     differently. It answers the step that has no answer yet, if there is one,
     and the messages that belong to no step.

     IT STOPS AT THE THANK-YOU AND DOES NOT DRAW IT, handing it back as
     `atThanks`, because the email and the phone go between the last question and
     the thank-you. The caller draws them and then the thank-you, so a chat drawn
     again reads in the order the customer lived through. */
  function drawFlowAnswers(flow, msgs, answers) {
    var byStep = {};
    var loose = [];
    for (var i = 0; i < msgs.length; i++) {
      var m = msgs[i];
      if (m.id && m.id > S.lastId) S.lastId = m.id;
      if (m.sender === 'customer' && m.step_id && !byStep[m.step_id]) byStep[m.step_id] = m;
      else loose.push(m);
    }

    var id = flow.first;
    var waitingOn = null;
    var atThanks = null;
    var guard = 0;
    while (id && guard++ < 50) {
      var s = flow.steps[id];
      if (!s) break;
      if (flow.thanks_step && id === flow.thanks_step) { atThanks = s; break; }
      addAskStep(s);
      if (s.kind === 'message') { id = s.next; continue; }
      var got = byStep[id];
      /* no messages came back at all: fall back to the answers on the row */
      var value = got ? got.body : (answers[id] && answers[id].value);
      if (!got && (value === undefined || value === null)) { waitingOn = s; break; }
      if (got && got.attachments && got.attachments.length) {
        addMineFiles(got.attachments.map(function (a) { return { name: a.name, preview: null }; }));
      } else if (s.kind === 'upload') {
        addMineFiles([{ name: String(value), preview: null }]);
      } else {
        addMine(String(value));
      }
      id = s.kind === 'choices' ? pickedNext(s, String(value)) : s.next;
    }
    return { waitingOn: waitingOn, loose: loose, atThanks: atThanks };
  }

  /* The lines that belong to no step: the team's replies, the system's own lines
     and anything the customer typed once the chat was live, in the order they
     were written. */
  function drawLoose(loose) {
    for (var j = 0; j < (loose || []).length; j++) {
      var n = loose[j];
      if (n.sender === 'team') addTeam(n.name, n.body);
      else if (n.sender === 'customer') addMine(n.body);
      else addSystem(n.body);
    }
  }

  /* The redraw above has usually drawn this question already (the bot's own
     message carries its step id), so only the composer is put back. If it has
     not, the question is drawn first rather than leaving a box with nothing
     above it. */
  function askAgain(step) {
    S.stepId = step.id;
    var last = D.log.querySelector('.sp-chat-ask:last-child');
    var shown = last && last.firstChild && last.firstChild.textContent === step.bold;
    if (!shown) addAskStep(step);
    if (step.kind === 'text') {
      composeBox(function (text, ctl) {
        ctl.taken();
        clearCompose();
        addMine(text);
        saveAnswer(step, text, function () { askStep(step.next); });
      });
    } else if (step.kind === 'choices') {
      composeChoices(step, function (choice) {
        addMine(choice.label);
        saveAnswer(step, choice.label, function () { askStep(choice.next); });
      });
    } else if (step.kind === 'upload') {
      composeUpload(step, function (files, undo) { sendFiles(step, files, undo); });
    } else {
      askStep(step.next);
    }
  }

  function pickedNext(step, value) {
    var choices = step.choices || [];
    for (var i = 0; i < choices.length; i++) {
      if (choices[i].label === value) return choices[i].next;
    }
    return step.next;
  }

  /* ========================================================= ORDER MANAGEMENT
   *
   * Lorenzo, 25 October 2026: "can we also add the order management? Linked with
   * the ERP instead of the Shopify?" ... "We need their email and then give them
   * the list of the orders we have fo them".
   *
   * It is NOT a list of questions like the Gorgias flows, so it does not use
   * `steps` at all. It goes: the same email question the flows ask -> a 6-digit
   * code to that email -> the customer's orders as cards -> one order -> Track,
   * Return (only when it can be returned) or Report issue -> Gorgias's own reply
   * -> either "Was this helpful?" or the message box to a person.
   *
   * WHY THE CODE EXISTS. Anybody can type anybody's email - a partner looking
   * for a surprise engagement ring, for instance - so the email alone is not
   * allowed to show what somebody ordered. The server also never says whether an
   * email is a customer: the line after the email is "IF we have orders under
   * that email".
   *
   * The words of every reply are Gorgias's and live in `sp-chat-flows.js`. The
   * action names here are the contract's, fixed with the server: `orders_start`,
   * `orders_resend`, `orders_verify`, `orders_list`, `order_choose`,
   * `orders_done`, and then the same `answer` and `finish` the flows use, with
   * the step ids `order_action`, `issue`, `helpful` and `details`.
   */

  var CODE_RE = /^\d{6}$/;

  /* The question that belongs above each of the four answers an order chat
     records. Used when the chat is drawn again on another page, so no answer is
     ever left sitting under nothing. */
  var ORDER_STEP_ASK = {
    order_action: 'order_actions_bold',
    issue: 'issue_bold',
    helpful: 'helpful_bold',
    details: 'more_help'
  };

  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
                'August', 'September', 'October', 'November', 'December'];

  /* '2026-10-12', or a whole ISO stamp, becomes '12 October 2026'.
     THE DATE IS READ AS THE DAY THE SERVER SENT and is never turned into a local
     time: a ship-by is a date, and a browser some hours west of us must not show
     the day before it. */
  function dayWords(iso) {
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso === null || iso === undefined ? '' : iso));
    if (!m) return '';
    return String(Number(m[3])) + ' ' + (MONTHS[Number(m[2]) - 1] || m[2]) + ' ' + m[1];
  }

  function ordersFlow() { return F.flows[F.home.orders]; }

  /* The ERP stores a channel in lower case ('shopify', 'etsy', 'direct'); a
     customer reads the shop's own name. Anything unknown is shown as it came. */
  var CHANNEL_WORDS = { shopify: 'Shopify', etsy: 'Etsy', direct: 'Direct' };
  function channelWords(c) {
    var k = String(c || '').toLowerCase();
    return CHANNEL_WORDS[k] || String(c || '');
  }

  function pickOrders() {
    var flow = ordersFlow();
    if (!flow) return;
    S.flow = flow;
    S.phase = 'orders';
    S.stepId = null;
    S.orders = null;
    S.order = null;
    S.verified = false;
    S.history = null;
    D.log.innerHTML = '';
    atHome(false);
    addMine(flow.label);
    askOrdersEmail();
  }

  /* THE SAME EMAIL QUESTION THE FLOWS ASK, word for word, because it is the same
     question - and because the email is what the code is sent to. */
  function askOrdersEmail() {
    addAsk(F.strings.email_bold, F.strings.email_italic);
    composeBox(function (text, ctl) {
      var email = text.trim();
      if (!EMAIL_RE.test(email) || email.length > 254) {
        ctl.keep(F.ui.bad_email);
        return;
      }
      ctl.taken();
      clearCompose();
      addMine(email);
      ordersStart(email);
    });
  }

  function ordersStart(email) {
    api('orders_start', { email: email, page: location.href }).then(function (res) {
      S.email = email;
      S.token = res.token;
      S.status = 'answering';
      if (!S.practice && S.token) writeToken(S.token);
      if (S.token) pushHistory(S.token, F.home.orders);
      /* Always the same line, whether or not a code went out. */
      addSystem(F.strings.orders_code_sent);
      askCode();
    }).catch(function () {
      addTrouble(function () { ordersStart(email); });
    });
  }

  function askCode() {
    addAsk(F.strings.code_bold, null);
    composeCode();
  }

  /* The code box. A NUMERIC keyboard on a phone and the one-time-code hint, so
     iOS and Android offer the code straight out of the email - and it is NOT
     FOCUSED, by Lorenzo's standing rule, so no keyboard opens until it is
     pressed. "Send a new code" sits quietly under it. */
  function composeCode() {
    clearCompose();
    var box = el('div', 'sp-chat-box sp-chat-codebox');
    var input = el('input', 'sp-chat-input sp-chat-code');
    input.type = 'text';
    input.setAttribute('inputmode', 'numeric');
    input.setAttribute('autocomplete', 'one-time-code');
    /* pattern keeps older iOS on the number pad; inputmode is what modern
       browsers read */
    input.setAttribute('pattern', '[0-9]*');
    input.setAttribute('aria-label', F.ui.code_label);
    input.placeholder = F.ui.code_label;
    input.maxLength = 6;

    var btn = el('button', 'sp-chat-send');
    btn.type = 'button';
    btn.setAttribute('aria-label', F.ui.send);
    btn.appendChild(iconSend());
    btn.disabled = true;

    var hint = el('div', 'sp-chat-hint');
    hint.hidden = true;

    function go() {
      var code = input.value.replace(/\D/g, '').slice(0, 6);
      if (!CODE_RE.test(code)) {
        hint.textContent = F.ui.code_wrong_shape;
        hint.hidden = false;
        scrollDown();
        return;
      }
      clearCompose();
      addMine(code);
      verifyCode(code);
    }

    input.addEventListener('input', function () {
      var only = input.value.replace(/\D/g, '').slice(0, 6);
      if (only !== input.value) input.value = only;
      btn.disabled = !CODE_RE.test(only);
      hint.hidden = true;
    });
    input.addEventListener('keydown', function (ev) {
      if (ev.key === 'Enter') { ev.preventDefault(); if (!btn.disabled) go(); }
    });
    btn.addEventListener('click', go);

    box.appendChild(input);
    box.appendChild(btn);
    D.compose.appendChild(box);
    D.compose.appendChild(hint);
    addQuietRow([{ label: F.ui.new_code, go: resendCode }]);
    scrollDown();
  }

  /* A new code. The answer is always ok, so the same line is said again - it
     cannot say whether a code actually went out. The box stays where it is. */
  function resendCode() {
    api('orders_resend', { token: S.token }).then(function () {
      addSystem(F.strings.orders_code_sent);
    }).catch(function () {
      addTrouble(resendCode);
    });
  }

  function verifyCode(code) {
    api('orders_verify', { token: S.token, code: code }).then(codeAnswer).catch(function (e) {
      /* the real server refuses a wrong, expired or stopped code with a 400 that
         carries the state - that is an answer to show, not a fault */
      if (e && e.data && e.data.state) { codeAnswer(e.data); return; }
      addTrouble(function () { verifyCode(code); });
    });
  }

  function codeAnswer(res) {
    if (res && res.ok) {
      S.verified = true;
      S.orders = res.orders || [];
      /* THE PAST CHATS COME BACK WITH THE ORDERS, in the one answer, so nothing
         else has to be asked for. This is the half that works on a phone the
         customer has never chatted from. */
      S.history = res.history || [];
      showOrders();
      return;
    }
    var state = (res && res.state) || 'bad_code';
    if (state === 'expired') { addSystem(F.strings.code_expired); overAndStartAgain(); return; }
    if (state === 'locked') { addSystem(F.strings.code_locked); overAndStartAgain(); return; }
    /* A wrong code can be tried again, so the question comes back with an
       empty box rather than the customer having to find it. */
    addSystem(F.strings.code_bad);
    askCode();
  }

  /* There is nothing more to do in this chat: an expired or stopped code, no
     orders under the email, or a chat that answered itself. The token is dropped
     so the next page does not try to bring it back. */
  function overAndStartAgain() {
    S.phase = 'over';
    stopPoll();
    dropToken();
    S.token = null;
    composeBackToStart();
  }

  /* THE ORDERS, as tappable cards. Also what "Back to my orders" draws, from the
     list the server already sent, so moving between orders asks nothing again. */
  function showOrders() {
    clearCompose();
    S.order = null;
    var orders = S.orders || [];
    var past = S.history || [];
    /* THE PAST CHATS GO ABOVE THE ORDER CARDS, as the design says. They are
       drawn from what verify sent, so moving between orders asks nothing again. */
    if (past.length) {
      var block = historyList(past, openServerPast);
      block.className = 'sp-chat-history sp-chat-history-inlog';
      D.log.appendChild(block);
    }
    if (!orders.length) {
      addSystem(F.strings.orders_none);
      /* WITH NO ORDERS BUT SOME PAST CHATS THE CHAT IS NOT OVER, and the token
         is kept: history_view needs it to open one. With neither there is
         nothing to read, so it ends as it did before. */
      if (!past.length) { overAndStartAgain(); return; }
      composeBackToStart();
      scrollDown();
      return;
    }
    var wrap = el('div', 'sp-chat-orders');
    orders.forEach(function (order) { wrap.appendChild(orderCard(order)); });
    D.log.appendChild(wrap);
    scrollDown();
  }

  /* Opening one of the verified customer's past chats, on any device. The id is
     the server's own and the server refuses any conversation that is not this
     customer's - that is the whole protection and it is not in this file. */
  function openServerPast(item) {
    if (!item || item.id === undefined || item.id === null) return;
    clearCompose();
    addMine(historyLabel(item.flow_key));
    api('history_view', { token: S.token, conversation_id: item.id })
      .then(function (res) {
        drawPast(res, (res && res.flow_key) || item.flow_key, true);
      })
      .catch(function () {
        addTrouble(function () { openServerPast(item); });
      });
  }

  /* `resume` does not answer the past chats - it answers one conversation - so a
     chat picked up on another page asks for them once, by its own action. A
     failure is not worth a line on screen: the orders are what the customer came
     for, and the list is drawn only if it arrives. */
  function relistHistory(done) {
    api('history_list', { token: S.token }).then(function (r) {
      S.history = (r && r.history) || [];
    }).catch(function () {
      S.history = [];
    }).then(done);
  }

  /* One card: the whole thing is the button, except the tracking link, which is a
     link and so cannot sit inside a button. */
  function orderCard(order) {
    var card = el('div', 'sp-chat-order');
    var b = el('button', 'sp-chat-order-main');
    b.type = 'button';
    b.setAttribute('aria-label', F.ui.orders_open + ' ' + (order.ref || ''));
    orderFacts(order, b);
    b.addEventListener('click', function () { chooseOrder(order); });
    card.appendChild(b);
    var track = trackingLine(order);
    if (track) card.appendChild(track);
    return card;
  }

  /* The facts of an order, in the order the README gives them: the reference the
     CUSTOMER knows and its channel, when it was ordered, the rings, where it is,
     and when it has to ship. */
  function orderFacts(order, into) {
    var top = el('div', 'sp-chat-order-top');
    top.appendChild(el('span', 'sp-chat-order-ref', order.ref || ''));
    if (order.channel) top.appendChild(el('span', 'sp-chat-order-channel', channelWords(order.channel)));
    into.appendChild(top);

    var placed = dayWords(order.placed_at);
    if (placed) into.appendChild(el('div', 'sp-chat-order-line', F.ui.orders_ordered + ' ' + placed));

    (order.items || []).forEach(function (item) {
      var row = el('div', 'sp-chat-order-item');
      row.appendChild(el('span', 'sp-chat-order-item-name', item.name || ''));
      if (item.detail) row.appendChild(el('span', 'sp-chat-order-item-detail', item.detail));
      into.appendChild(row);
    });

    if (order.stage) into.appendChild(el('div', 'sp-chat-order-stage', order.stage));

    var shipBy = dayWords(order.ship_by);
    if (shipBy) into.appendChild(el('div', 'sp-chat-order-line', F.ui.orders_ship_by + ' ' + shipBy));
  }

  /* The tracking link, once there is one. With a number but no address it is
     still said, as text, rather than dropped. */
  function trackingLine(order) {
    var t = order.tracking;
    if (!t) return null;
    var detail = [t.carrier, t.number].filter(function (x) { return !!x; }).join(' ');
    if (t.url) {
      var a = el('a', 'sp-chat-order-track');
      a.href = t.url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.appendChild(el('span', null, F.ui.orders_tracking));
      if (detail) a.appendChild(el('span', 'sp-chat-order-num', detail));
      return a;
    }
    if (!detail) return null;
    return el('div', 'sp-chat-order-track sp-chat-order-track-flat',
              F.ui.orders_tracking + ' ' + detail);
  }

  /* Choosing a card. The server is told which order the chat is about and
     refuses any order that is not this customer's, so the order drawn is the one
     IT sent back, not the one the card was drawn from. */
  function chooseOrder(order, again) {
    clearCompose();
    if (!again) addMine(order.ref || '');
    api('order_choose', { token: S.token, order_id: order.id }).then(function (res) {
      S.order = (res && res.order) || order;
      showOrder(S.order);
    }).catch(function () {
      addTrouble(function () { chooseOrder(order, true); });
    });
  }

  function showOrder(order) {
    var detail = el('div', 'sp-chat-order-detail');
    orderFacts(order, detail);
    var track = trackingLine(order);
    if (track) detail.appendChild(track);
    D.log.appendChild(detail);
    scrollDown();
    askOrderAction(order);
  }

  /* The three actions, with Gorgias's labels. NEVER CANCEL ORDER: Gorgias has it
     switched on with no status picked and no reply written, so it is offered
     nowhere, and ours copies that. Return order waits for `can_return`, which is
     Gorgias's "delivered less than 30 days ago". */
  function askOrderAction(order) {
    addAsk(F.strings.order_actions_bold, null);
    var actions = (S.flow.actions || []).filter(function (a) {
      return a.needs !== 'can_return' || !!order.can_return;
    });
    composeChoices({ choices: actions }, function (choice) {
      addMine(choice.label);
      saveStep('order_action', F.strings.order_actions_bold, choice.label, function () {
        if (choice.id === 'track') doTrack(order);
        else if (choice.id === 'return') doReturn();
        else doIssue(order);
      });
    }, backToOrdersExtra());
  }

  /* "Back to my orders", offered under an answer so somebody with several orders
     can move on. It is NOT offered once the chat is over - after "Yes, thanks"
     the server has closed the conversation, and after `finish` the chat belongs
     to a person - because the token would be refused and the button would lie. */
  function backToOrdersExtra() {
    if (!S.verified || !S.orders || !S.orders.length) return null;
    return [{
      label: F.ui.back_to_orders,
      /* what was pressed is echoed as the customer's own pill, the same as every
         other press, so the question it was pressed instead of answering is not
         left hanging over a list of orders with nothing under it */
      go: function () { clearCompose(); addMine(F.ui.back_to_orders); showOrders(); }
    }];
  }

  function hasShipped(order) {
    return !!(order.shipped_at || order.delivered_at || (order.tracking && order.tracking.number));
  }

  /* TRACK ORDER. The facts first - where it is, when it has to ship, the tracking
     link - and then, ONLY for an order that has not shipped, Gorgias's reply word
     for word, because that reply says the ring is still being made. */
  function doTrack(order) {
    var block = el('div', 'sp-chat-track');
    if (order.stage) block.appendChild(el('div', 'sp-chat-order-stage', order.stage));
    var shipBy = dayWords(order.ship_by);
    if (shipBy) block.appendChild(el('div', 'sp-chat-order-line', F.ui.orders_ship_by + ' ' + shipBy));
    var track = trackingLine(order);
    if (track) block.appendChild(track);
    if (block.childNodes.length) { D.log.appendChild(block); scrollDown(); }
    if (!hasShipped(order)) addBot(S.flow.track_not_shipped_reply);
    askHelpful();
  }

  /* RETURN ORDER. Gorgias's reply word for word, with its paragraph breaks and
     the address on its own line. `addBot` keeps them. */
  function doReturn() {
    addBot(S.flow.return_reply);
    askHelpful();
  }

  /* REPORT ISSUE. The options of the order's own scenario, in Gorgias's order,
     with Gorgias's labels. An unknown scenario falls back to Fallback, which is
     what Gorgias's Fallback is for. */
  function doIssue(order) {
    var scenarios = S.flow.scenarios || {};
    var scenario = scenarios[order.scenario] || scenarios.fallback;
    if (!scenario) { askDetails(F.strings.issue_bold); return; }
    addAsk(F.strings.issue_bold, null);
    composeChoices({ choices: scenario.options }, function (option) {
      addMine(option.label);
      saveStep('issue', F.strings.issue_bold, option.label, function () {
        addBot(option.reply);
        /* Gorgias's switch decides what happens now: an option whose reply ASKS
           the customer something has to reach a person, and one that answers
           outright asks whether it helped. */
        if (option.helpful) askHelpful();
        else askDetails(option.reply);
      });
    }, backToOrdersExtra());
  }

  function askHelpful() {
    addAsk(F.strings.helpful_bold, null);
    composeChoices({
      choices: [
        { id: 'yes', label: F.ui.helpful_yes },
        { id: 'more', label: F.ui.helpful_more }
      ]
    }, function (choice) {
      addMine(choice.label);
      saveStep('helpful', F.strings.helpful_bold, choice.label, function () {
        if (choice.id === 'yes') { ordersDone(); return; }
        addAsk(F.strings.more_help, null);
        askDetails(F.strings.more_help);
      });
    }, backToOrdersExtra());
  }

  /* The message box for what a person needs to read. What they type is recorded
     as the `details` answer and then the chat FINISHES exactly the way the flows
     finish: live if somebody is on, email if nobody is. */
  function askDetails(question) {
    composeBox(function (text, ctl) {
      ctl.taken();
      clearCompose();
      addMine(text);
      saveStep('details', question, text, function () { finishChat(); });
    }, backToOrdersExtra());
  }

  /* "Yes, thanks": the chat answered itself. The contract - closed,
     `closed_by = 'self_served'`, and NO TICKET, EVER. The conversation is closed
     server side, so the token is finished with and the only way on is to start
     again. */
  function ordersDone() {
    api('orders_done', { token: S.token }).then(function () {
      S.status = 'closed';
      addSystem(F.strings.self_served_end);
      overAndStartAgain();
    }).catch(function () {
      addTrouble(ordersDone);
    });
  }

  /* One answer saved on its own, by step id: the orders chat has no `steps`, so
     it cannot use saveAnswer. Same `answer` action, same contract. */
  function saveStep(stepId, question, value, done) {
    S.stepId = stepId;
    api('answer', {
      token: S.token,
      step_id: stepId,
      question: question,
      value: value
    }).then(function () {
      done();
    }).catch(function () {
      addTrouble(function () { saveStep(stepId, question, value, done); });
    });
  }

  /* ------------------------------------ an orders chat carried onto another page
   *
   * `resume` answers `flow_key: 'orders'`. What can be put back honestly depends
   * on what it hands over:
   *
   * - A chat that is WAITING or LIVE is redrawn in full - the answers given, each
   *   under its question, then the team's replies - because from here on it is an
   *   ordinary live chat.
   * - A chat still being answered needs the verified customer's ORDERS to be
   *   drawn at all, and the contract does not promise them back from `resume`. If
   *   they are there (or `orders_list` gives them, which is what that action is
   *   for), the list is drawn again. If they are not, THE ORDERS CHAT STARTS AGAIN
   *   FROM THE EMAIL rather than being drawn wrong: a half-drawn list of somebody
   *   else's orders would be a great deal worse than one extra code.
   */
  function redrawOrders(res) {
    var flow = ordersFlow();
    S.flow = flow;
    S.phase = 'orders';
    S.email = res.email || '';
    S.status = res.status;
    D.log.innerHTML = '';
    atHome(false);
    addMine(flow.label);
    addAsk(F.strings.email_bold, F.strings.email_italic);
    if (S.email) addMine(S.email);

    var msgs = res.messages || [];
    var answers = res.answers || {};
    var loose = drawOrderAnswers(msgs, answers);

    if (res.status === 'waiting' || res.status === 'live') {
      addSystem(F.strings.after_thanks_live);
      drawLoose(loose);
      S.phase = 'live';
      liveCompose();
      restartPoll();
      return;
    }

    var orders = (res.orders && res.orders.length !== undefined) ? res.orders : null;
    if (!res.verified_at && !orders) { startOrdersAgain(); return; }
    S.verified = true;
    if (orders) {
      S.orders = orders;
      relistHistory(showOrders);
      return;
    }
    api('orders_list', { token: S.token }).then(function (r) {
      if (r && r.ok && r.orders && r.orders.length !== undefined) {
        S.orders = r.orders;
        relistHistory(showOrders);
      } else {
        startOrdersAgain();
      }
    }).catch(function () {
      startOrdersAgain();
    });
  }

  /* Every answer the customer gave, each under the question it answered, in the
     order the messages were written. Returns the messages that belong to no step
     - the team's, the system's, and anything typed while live - so the caller can
     draw them after. */
  function drawOrderAnswers(msgs, answers) {
    var loose = [];
    for (var i = 0; i < msgs.length; i++) {
      var m = msgs[i];
      if (m.id && m.id > S.lastId) S.lastId = m.id;
      if (m.sender !== 'customer' || !m.step_id) { loose.push(m); continue; }
      var stored = answers[m.step_id];
      var asked = (stored && stored.question)
        || (ORDER_STEP_ASK[m.step_id] ? F.strings[ORDER_STEP_ASK[m.step_id]] : null);
      if (asked) addAsk(asked, null);
      addMine(m.body);
    }
    return loose;
  }

  /* Starting the orders chat again, saying so rather than silently asking for an
     email a second time. The old conversation is left exactly as the server has
     it; `orders_start` opens a new one. */
  function startOrdersAgain() {
    dropToken();
    S.token = null;
    S.verified = false;
    S.orders = null;
    S.order = null;
    S.history = null;
    addSystem(F.strings.orders_again);
    askOrdersEmail();
  }

  /* ============================================ ANSWERING THE THEME'S COMMANDS
   *
   * Lorenzo, 25 October 2026: "move the chat to the draft 5.13.7 and disable the
   * Gorgias all together". The theme's own sections drive the Gorgias widget
   * directly, and were read on the drafts the same day:
   *
   *   GorgiasChat.init()           6 places (custom-ring-hero, custom-faq-section,
   *                                sp-make-it-yours), each as `window.GorgiasChat
   *                                ? init() : wait for gorgias-widget-loaded`
   *   GorgiasChat.open()          12 places, always after that promise
   *   GorgiasChat.sendMessage(x)   3 places, from `[data-gorgias-flow]` buttons in
   *                                custom-ring-hero, x being the flow's own name
   *   #open-gorgias-chat          about 40 links across the shop
   *   [data-spdr-chat]             the wedding page's button, whose own script
   *                                calls GorgiasChat.open()
   *   gorgias-widget-loaded        the event all three sections fall back to
   *
   * With the Gorgias embed off, every one of those would silently take the
   * shopper to the custom request form instead. So this file answers to the same
   * commands and NOBODY'S SECTION HAS TO CHANGE.
   *
   * IT DEFINES window.GorgiasChat ONLY IF THERE IS NONE. If the real Gorgias
   * widget is ever on the same page it keeps its own object and this one is never
   * written - the two must never fight over the name.
   */

  /* Ignoring case and spaces, which is what "equals a flow's label" has to mean:
     the flow name is typed into the theme editor by hand, and "Heirloom gold"
     and "heirloom  gold" are the same answer. */
  function flatLabel(s) {
    return String(s === undefined || s === null ? '' : s).replace(/\s+/g, '').toLowerCase();
  }

  /* The flow whose label is this text, or null. The home screen's own rows and
     the order row, which are the labels anybody could be naming. */
  function flowKeyForLabel(text) {
    var want = flatLabel(text);
    if (!want) return null;
    var keys = (F.home.flows || []).slice();
    if (ordersShown()) keys.push(F.home.orders);
    for (var i = 0; i < keys.length; i++) {
      var flow = F.flows[keys[i]];
      if (flow && flow.label && flatLabel(flow.label) === want) return keys[i];
    }
    return null;
  }

  /* Starting a flow BY COMMAND. The same one twice inside a moment is ignored,
     so two document listeners on one page - custom-ring-hero binds its own -
     cannot start it twice. A row pressed on the home screen does not come
     through here, so pressing the same row twice still starts again. */
  function startFlowByCommand(key) {
    if (!key) return false;
    var now = Date.now();
    if (S.lastStartKey === key && (now - S.lastStartAt) < SAME_FLOW_MS) return true;
    S.lastStartKey = key;
    S.lastStartAt = now;
    if (key === F.home.orders) pickOrders();
    else pickFlow(key);
    return true;
  }

  /* ------------------------------------------------------------- page mode
   *
   * One page that is one flow and nothing else, on Lorenzo's word about the
   * wedding-dress page: "we dont want to show them the customise ring or any
   * other chat option, we start with a chat about wedding rings".
   *
   * IT IS ONLY ON FOR A FLOW THAT EXISTS AND HAS STEPS. The wedding flow is a
   * placeholder until his questions arrive, so until then that page behaves like
   * every other one: the home screen, the four flows and the order row. Nothing
   * about the flow is invented here or in the flow file.
   */
  function pageFlowFromPath() {
    var pages = F && F.pages;
    if (!pages) return null;
    var path = '';
    try { path = String(window.location.pathname || ''); } catch (e) { return null; }
    /* Shopify serves the page without a trailing slash; one that arrives with
       one is the same page, so it is not a different answer. */
    if (path.length > 1 && path.charAt(path.length - 1) === '/') path = path.slice(0, -1);
    if (!Object.prototype.hasOwnProperty.call(pages, path)) return null;
    var key = pages[path];
    return flowHasSteps(F.flows[key]) ? key : null;
  }

  /* Into the page's own flow. It does NOTHING when a conversation is already on
     screen - one carried over from another page, or one already going - because
     throwing that away would lose what the customer has said. */
  function enterPageFlow() {
    if (!S.pageFlowKey) return false;
    /* A FLOW IS ALREADY CHOSEN, OR A CONVERSATION IS ALREADY DRAWN. `S.flow` is
       the one that matters and it is set the moment pickFlow runs, before
       `start` has answered and before anything is on screen - without it,
       openChat() and openForCommand() each called this in the same tick and the
       page's flow was started TWICE, which drew its first question twice. Seen
       on 25 October 2026 in the page-mode harness, and only seen because that
       flow has no label and so draws nothing of its own at once. */
    if (S.flow || S.token || D.log.firstChild) return false;
    /* `resume` may be about to hand one back. Show the conversation side, empty,
       rather than the home screen, and start the flow when it answers. */
    if (S.resuming) { atHome(false); return false; }
    pickFlow(S.pageFlowKey);
    return true;
  }

  /* What `GorgiasChat.open()`, the launcher and a chat link all end at. */
  function openForCommand() {
    openChat();
    if (S.pageFlowKey) enterPageFlow();
  }

  function defineGorgiasChat() {
    if (window.GorgiasChat) return false;
    /* Everything the real widget has that nobody here needs: it must not throw
       when a theme, an app or a tag manager calls it. */
    function noop() { return undefined; }
    function self() { return shim; }
    var shim = {
      /* ---- what the theme actually calls */
      init: function () { return Promise.resolve(shim); },
      open: function () { openForCommand(); return shim; },
      close: function () { closeChat(); return shim; },
      isOpen: function () { return !!S.open; },
      /* The flow's own name opens that flow; anything else just opens the chat,
         which is what Gorgias does with a message it has no flow for. IN PAGE
         MODE IT ALWAYS OPENS THAT PAGE'S FLOW: no other flow is offered there,
         so a name is not a way round it. */
      sendMessage: function (text) {
        if (S.pageFlowKey) { openForCommand(); return shim; }
        var key = flowKeyForLabel(text);
        if (!key) { openForCommand(); return shim; }
        openChat();
        startFlowByCommand(key);
        return shim;
      },

      /* ---- harmless no-ops. None of these is called by the theme today; they
         are the rest of the Gorgias widget's own surface, so a call from
         anywhere else is answered and nothing breaks. `on`/`off`/`once` hand
         back a function, which is what a listener registration is expected to
         give, and it does nothing either. */
      on: function () { return noop; },
      off: noop,
      once: function () { return noop; },
      hidePoweredBy: noop,
      showPoweredBy: noop,
      setCurrentUser: noop,
      captureUserEmail: noop,
      identify: noop,
      setUserData: noop,
      setContactFormFields: noop,
      sendCustomEvent: noop,
      openArticle: noop,
      show: self,
      hide: self,
      hideChat: self,
      showChat: self,
      toggle: self,
      destroy: noop,
      unreadCount: function () { return 0; },
      getUnreadCount: function () { return 0; },
      /* so anybody reading the console can see whose object this is */
      isSestoShim: true
    };
    window.GorgiasChat = shim;
    return true;
  }

  /* The event all three theme sections fall back to when `window.GorgiasChat` is
     not there yet. Fired once, when this file is ready. */
  function fireReady() {
    var ev;
    try {
      ev = new Event(GORGIAS_EVENT);
    } catch (e) {
      try {
        ev = document.createEvent('Event');
        ev.initEvent(GORGIAS_EVENT, true, true);
      } catch (e2) { return; }
    }
    try { window.dispatchEvent(ev); } catch (e3) { /* nothing */ }
  }

  /* THE SHOP'S OWN CHAT LINKS. About forty `#open-gorgias-chat` links and the
     `[data-gorgias-flow]` buttons in custom-ring-hero.
     ONLY WHEN NOBODY ELSE HAS HANDLED IT. custom-ring-hero, custom-faq-section
     and sp-make-it-yours each bind their own document listener and call
     preventDefault; they are in the page before this file, so theirs runs first
     and `defaultPrevented` is already true by the time this one is asked. Doing
     nothing then is the right answer, and it is also what stops a flow being
     started twice. */
  function bindChatLinks() {
    document.addEventListener('click', function (ev) {
      if (ev.defaultPrevented) return;
      var t = ev.target;
      if (!t || !t.closest) return;
      var flowEl = t.closest('[data-gorgias-flow]');
      var linkEl = flowEl ? null : t.closest('a[href="#open-gorgias-chat"]');
      if (!flowEl && !linkEl) return;
      ev.preventDefault();
      if (S.pageFlowKey) { openForCommand(); return; }
      openChat();
      if (!flowEl) return;
      var key = flowKeyForLabel(flowEl.getAttribute('data-gorgias-flow'));
      if (key) startFlowByCommand(key);
    });
  }

  /* ------------------------------------------------------------------- boot */

  function practiceFromPage(root) {
    var asked = null;
    try {
      var m = /[?&]sp_chat_practice=([^&#]*)/.exec(window.location.search || '');
      if (m) asked = decodeURIComponent(m[1]).toLowerCase();
    } catch (e) { asked = null; }
    if (!asked) asked = (root.getAttribute('data-sp-chat-practice') || '').toLowerCase();
    if (asked === 'offline' || asked === 'email' || asked === '0') return 'offline';
    /* `1` is what docs/chat/README.md writes; the two named modes are newer */
    if (asked === 'online' || asked === 'live' || asked === '1' || asked === 'true') return 'online';
    return null;
  }

  function init(root) {
    root = root || document.getElementById(ROOT_ID);
    if (!root) return null;
    F = window.SPChatFlows;
    if (!F) return null;                   /* the flow file has to be there */
    if (S.ready && S.root === root) return window.SPChat;

    S.root = root;
    /* an attribute that is there but EMPTY means "no function", which is not
       the same as an attribute that is missing */
    S.fnUrl = root.hasAttribute('data-sp-chat-fn')
      ? (root.getAttribute('data-sp-chat-fn') || '')
      : FN_DEFAULT;
    S.logoUrl = root.getAttribute('data-sp-chat-logo') || '';
    S.privacyUrl = root.getAttribute('data-sp-chat-privacy') || PRIVACY_DEFAULT;
    S.practice = practiceFromPage(root);
    /* With no address there is nothing to call, so the chat practises rather
       than failing on every step. This is what makes the preview's "calls no
       server" true by construction and not by luck. */
    if (!S.fnUrl && !S.practice) S.practice = 'online';
    /* practice mode shows the history screens too, from the invented chats in
       the flow file, so the preview demonstrates them without writing anything
       to the browser it is being looked at in */
    if (S.practice) S.practiceHistory = practiceOwnHistory();
    /* BEFORE build(): the home screen's rows and the header's back arrow are
       both drawn differently on a page that is one flow. */
    S.pageFlowKey = pageFlowFromPath();
    S.ready = true;

    build();
    D.badge.hidden = !S.practice;

    window.addEventListener('resize', fitViewport);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', fitViewport);
      window.visualViewport.addEventListener('scroll', fitViewport);
    }
    document.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape' && S.open) closeChat();
    });

    /* practice is per page load; nothing is stored and nothing is resumed */
    if (!S.practice) resume();

    /* THE THEME'S COMMANDS, last: the object, the links, then the event that
       says both are ready. Only if no Gorgias widget already holds the name. */
    defineGorgiasChat();
    bindChatLinks();
    fireReady();

    return window.SPChat;
  }

  window.SPChat = {
    version: '4 (25 October 2026: email and phone at the end, four flows, page mode)',
    init: function (root) { return init(root); },
    open: function () { if (S.ready) openForCommand(); },
    close: function () { closeChat(); },
    reset: function () { if (S.ready) reset(); },
    /* for the preview page only: switch practice mode without a reload */
    setPractice: function (mode) {
      S.practice = (mode === 'offline') ? 'offline' : (mode ? 'online' : null);
      S.practiceHistory = S.practice ? practiceOwnHistory() : null;
      if (S.ready) { D.badge.hidden = !S.practice; reset(); }
      return S.practice;
    },
    /* the state, so a preview can be driven from the console */
    state: S
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(null); });
  } else {
    init(null);
  }
}());
